import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import SignatureCanvas from "react-signature-canvas";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import AppShell from "../components/layout/AppShell";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Icon from "../components/ui/Icon";
import Modal from "../components/ui/Modal";
import BrandingSettings from "../components/settings/BrandingSettings";
import { Parse } from "../lib/parse";
import { applyPreferredLanguage } from "../i18n";
import { useAlert } from "../contexts/AlertContext";

const parseStoredJson = (key) => {
  try {
    const rawValue = localStorage.getItem(key);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch (error) {
    return null;
  }
};

const resolveServerBaseUrl = () => {
  const rawBaseUrl = String(localStorage.getItem("baseUrl") || "").trim();
  if (!rawBaseUrl) {
    return window.location.origin;
  }

  const withProtocol = rawBaseUrl.startsWith(":")
    ? `${window.location.protocol}//${window.location.hostname}${rawBaseUrl}`
    : rawBaseUrl;

  const trimmed = withProtocol.replace(/\/+$/, "");
  if (trimmed.endsWith("/app")) {
    return trimmed.slice(0, -4) || window.location.origin;
  }
  return trimmed;
};

const DEFAULT_SIGNATURE_TYPES = [
  { name: "draw", enabled: true },
  { name: "typed", enabled: true },
  { name: "upload", enabled: true },
  { name: "default", enabled: true }
];

const DEFAULT_SIGNATURE_VALIDATION = {
  minArea: 1500,
  minLength: 180,
  minAreaFloor: 600,
  minLengthFloor: 80,
  maxArea: 30000,
  maxLength: 2000
};

const SIGNATURE_COLOR_OPTIONS = ["#1f2937", "#2563eb", "#dc2626"];
const SIGNATURE_CANVAS_HEIGHT = 180;
const INITIALS_CANVAS_HEIGHT = 140;
const MIN_CANVAS_WIDTH = 280;

const DATE_FORMAT_OPTIONS = [
  "MM/DD/YYYY",
  "MMMM DD, YYYY",
  "DD MMMM, YYYY",
  "DD-MM-YYYY",
  "DD MMM, YYYY",
  "YYYY-MM-DD",
  "MM-DD-YYYY",
  "MM.DD.YYYY",
  "MMM DD, YYYY"
];

const EMAIL_EDITOR_MODULES = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["blockquote", "code-block"],
    ["link"],
    ["clean"]
  ],
  history: {
    delay: 500,
    maxStack: 100,
    userOnly: true
  }
};

const EMAIL_EDITOR_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "align",
  "blockquote",
  "code-block",
  "link"
];

const EMAIL_TEMPLATE_VARIABLES = [
  {
    token: "{{signing_url}}",
    labelKey: "new-ui.settings.email.variables.signing-url",
    labelFallback: "Signing URL"
  },
  {
    token: "{{sender_name}}",
    labelKey: "new-ui.settings.email.variables.sender-name",
    labelFallback: "Sender name"
  },
  {
    token: "{{receiver_name}}",
    labelKey: "new-ui.settings.email.variables.receiver-name",
    labelFallback: "Receiver name"
  },
  {
    token: "{{document_title}}",
    labelKey: "new-ui.settings.email.variables.document-title",
    labelFallback: "Document title"
  },
  {
    token: "{{note}}",
    labelKey: "new-ui.settings.email.variables.note",
    labelFallback: "Custom message"
  }
];

const EMAIL_TEMPLATE_MODE = {
  HTML: "html",
  PLAIN: "plain"
};

const EMAIL_TEMPLATE_CONTENT_START = "<!-- DANA_CUSTOM_CONTENT_START -->";
const EMAIL_TEMPLATE_CONTENT_END = "<!-- DANA_CUSTOM_CONTENT_END -->";
const DEFAULT_TENANT_LOGO_URL = "https://imgcdn.email-platform.com/danaconnect/logo-danasign.png";
const DEFAULT_TENANT_THEME_COLOR = "#ef4b26";
const TENANT_LOGO_MAX_SIZE_MB = 4;

const normalizeEmailTemplateMode = (value) => {
  const normalized = String(value || "").toLowerCase();
  return normalized === EMAIL_TEMPLATE_MODE.PLAIN ? EMAIL_TEMPLATE_MODE.PLAIN : EMAIL_TEMPLATE_MODE.HTML;
};

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const plainTextToHtml = (value) =>
  String(value || "")
    .split(/\r?\n/)
    .map((line) => {
      const trimmedLine = line.trim();
      return trimmedLine ? `<p>${escapeHtml(trimmedLine)}</p>` : "<p><br/></p>";
    })
    .join("");

const htmlToPlainText = (value) => {
  const html = String(value || "");
  if (!html) {
    return "";
  }
  if (typeof window !== "undefined" && window.document) {
    const parser = new DOMParser();
    const parsed = parser.parseFromString(html, "text/html");
    return String(parsed.body?.textContent || "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const isRichHtmlEmpty = (html) => {
  const plain = htmlToPlainText(html);
  return !plain.trim();
};

const isTemplateBodyEmpty = (mode, body) => {
  if (mode === EMAIL_TEMPLATE_MODE.PLAIN) {
    return !String(body || "").trim();
  }
  return isRichHtmlEmpty(body);
};

const extractTemplateContent = (value) => {
  const raw = String(value || "");
  const startIndex = raw.indexOf(EMAIL_TEMPLATE_CONTENT_START);
  const endIndex = raw.indexOf(EMAIL_TEMPLATE_CONTENT_END);
  if (startIndex !== -1 && endIndex !== -1 && endIndex > startIndex) {
    return raw
      .slice(startIndex + EMAIL_TEMPLATE_CONTENT_START.length, endIndex)
      .trim();
  }
  return unwrapHtmlBody(raw);
};

const isValidHexColor = (value) => /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test(String(value || "").trim());

const normalizeHexColor = (value, fallback = DEFAULT_TENANT_THEME_COLOR) => {
  const raw = String(value || "").trim();
  if (!isValidHexColor(raw)) {
    return fallback;
  }
  if (raw.length === 4) {
    return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`.toLowerCase();
  }
  return raw.toLowerCase();
};

const darkenHexColor = (hexColor, factor = 0.14) => {
  const normalized = normalizeHexColor(hexColor);
  const r = Math.max(0, Math.min(255, Math.round(parseInt(normalized.slice(1, 3), 16) * (1 - factor))));
  const g = Math.max(0, Math.min(255, Math.round(parseInt(normalized.slice(3, 5), 16) * (1 - factor))));
  const b = Math.max(0, Math.min(255, Math.round(parseInt(normalized.slice(5, 7), 16) * (1 - factor))));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b
    .toString(16)
    .padStart(2, "0")}`;
};

const applyTenantBrandColor = (color) => {
  if (typeof document === "undefined") {
    return;
  }
  const normalized = normalizeHexColor(color);
  document.documentElement.style.setProperty("--app-brand", normalized);
  document.documentElement.style.setProperty("--app-brand-dark", darkenHexColor(normalized));
};

const resolveTenantBrandingFromStorage = () => {
  const extUser = getExtUser();
  const tenant = extUser?.TenantId || {};
  const logo = String(tenant?.Logo || localStorage.getItem("appLogo") || DEFAULT_TENANT_LOGO_URL).trim();
  const color = normalizeHexColor(
    tenant?.PrimaryColor || tenant?.primaryColor || tenant?.ThemeColor || tenant?.themeColor || DEFAULT_TENANT_THEME_COLOR
  );
  return {
    logo,
    color
  };
};

const buildRequestEmailShell = (contentHtml, shellCopy, _branding) =>
  `<html><head><meta http-equiv='Content-Type' content='text/html; charset=UTF-8' /></head><body><div style='background-color:#f5f7fb;padding:20px'><div style='background:#ffffff;border-radius:8px;overflow:hidden'><div style='padding:14px 18px'><img src='{{tenant_logo_url}}' height='44' alt='logo' /></div><div style='padding:8px 18px;font-family:system-ui;background-color:{{tenant_theme_color}}'><p style='font-size:18px;font-weight:500;color:#ffffff;margin:0'>${shellCopy.requestTitle}</p></div><div style='padding:20px;font-family:system-ui;font-size:14px;color:#1f2b3a'>${EMAIL_TEMPLATE_CONTENT_START}${contentHtml}${EMAIL_TEMPLATE_CONTENT_END}<div style='text-align:center;margin-top:24px'>{{signing_url}}</div></div></div><div style='padding-top:12px'><p style='margin:0;font-family:system-ui;font-size:12px;color:#637389'>${shellCopy.footer}</p></div></div></body></html>`;

const buildCompletionEmailShell = (contentHtml, shellCopy, _branding) =>
  `<html><head><meta http-equiv='Content-Type' content='text/html; charset=UTF-8' /></head><body><div style='background-color:#f5f7fb;padding:20px'><div style='background:#ffffff;border-radius:8px;overflow:hidden'><div style='padding:14px 18px'><img src='{{tenant_logo_url}}' height='44' alt='logo' /></div><div style='padding:8px 18px;font-family:system-ui;background-color:{{tenant_theme_color}}'><p style='font-size:18px;font-weight:500;color:#ffffff;margin:0'>${shellCopy.completionTitle}</p></div><div style='padding:20px;font-family:system-ui;font-size:14px;color:#1f2b3a'>${EMAIL_TEMPLATE_CONTENT_START}${contentHtml}${EMAIL_TEMPLATE_CONTENT_END}</div></div><div style='padding-top:12px'><p style='margin:0;font-family:system-ui;font-size:12px;color:#637389'>${shellCopy.footer}</p></div></div></body></html>`;

const ensureRequestSigningUrlPlaceholder = (body) => {
  const normalized = String(body || "").trim();
  if (!normalized) {
    return "{{signing_url}}";
  }
  if (normalized.includes("{{signing_url}}")) {
    return normalized;
  }
  return `${normalized}\n\n{{signing_url}}`;
};

const buildStoredTemplateBody = (kind, mode, editableBody, shellCopy, branding) => {
  const source = String(editableBody || "").trim();
  if (mode === EMAIL_TEMPLATE_MODE.PLAIN) {
    if (kind === "request") {
      return ensureRequestSigningUrlPlaceholder(source);
    }
    return source;
  }
  const contentHtml = source || "<p><br/></p>";
  return kind === "request"
    ? buildRequestEmailShell(contentHtml, shellCopy, branding)
    : buildCompletionEmailShell(contentHtml, shellCopy, branding);
};

const convertTemplateBodyBetweenModes = (body, currentMode, nextMode) => {
  if (currentMode === nextMode) {
    return body;
  }
  if (nextMode === EMAIL_TEMPLATE_MODE.PLAIN) {
    return htmlToPlainText(body);
  }
  return plainTextToHtml(body);
};

const replaceTemplateVariables = (template, variables) => {
  let output = String(template || "");
  Object.keys(variables || {}).forEach((variableName) => {
    const regex = new RegExp(`{{${variableName}}}`, "g");
    output = output.replace(regex, variables[variableName]);
  });
  return output;
};

const disablePreviewLinks = (html) => {
  const content = String(html || "");
  if (!content) {
    return content;
  }
  const lockStyle =
    "<style>a,a *{pointer-events:none !important;cursor:default !important;}a{text-decoration:none !important;}</style>";
  if (/<\/head>/i.test(content)) {
    return content.replace(/<\/head>/i, `${lockStyle}</head>`);
  }
  return `${lockStyle}${content}`;
};

const getExtUser = () => {
  const extClass = parseStoredJson("Extand_Class");
  return Array.isArray(extClass) ? extClass[0] || {} : {};
};

const isAdminRole = (role) => role === "contracts_Admin" || role === "contracts_OrgAdmin";
const ADMIN_PERSONAL_EMAIL_ROLES = new Set(["contracts_Admin", "contracts_OrgAdmin"]);
const EDITOR_PERSONAL_EMAIL_ROLES = new Set(["contracts_Editor"]);
const USER_PERSONAL_EMAIL_ROLES = new Set(["contracts_User"]);

const resolveTenantPersonalEmailPermissions = (tenant = {}) => {
  const hasAdminPermission = tenant?.AllowPersonalEmailTemplatesForAdmin !== undefined;
  const hasEditorPermission = tenant?.AllowPersonalEmailTemplatesForEditor !== undefined;
  const hasUserPermission = tenant?.AllowPersonalEmailTemplatesForUser !== undefined;
  const legacyPermission = Boolean(tenant?.AllowPersonalEmailTemplatesForEditorOrgAdmin);

  return {
    allowAdmin: hasAdminPermission
      ? Boolean(tenant?.AllowPersonalEmailTemplatesForAdmin)
      : legacyPermission,
    allowEditor: hasEditorPermission
      ? Boolean(tenant?.AllowPersonalEmailTemplatesForEditor)
      : legacyPermission,
    allowUser: hasUserPermission ? Boolean(tenant?.AllowPersonalEmailTemplatesForUser) : false
  };
};

const canUsePersonalEmailTemplatesByRole = (
  role,
  { allowAdmin = false, allowEditor = false, allowUser = false } = {}
) => {
  if (ADMIN_PERSONAL_EMAIL_ROLES.has(role)) {
    return Boolean(allowAdmin);
  }
  if (EDITOR_PERSONAL_EMAIL_ROLES.has(role)) {
    return Boolean(allowEditor);
  }
  if (USER_PERSONAL_EMAIL_ROLES.has(role)) {
    return Boolean(allowUser);
  }
  return false;
};

const normalizeCloudArray = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.result)) {
    return payload.result;
  }
  return [];
};

const formatUserRole = (role, t) => {
  if (!role) {
    return t("new-ui.dashboard.no-data", { defaultValue: "No data" });
  }
  return String(role).replace(/^contracts_/i, "").replace(/_/g, " ").trim();
};

const USER_ROLE_EDIT_OPTIONS = [
  { value: "OrgAdmin", raw: "contracts_OrgAdmin", label: "OrgAdmin" },
  { value: "Editor", raw: "contracts_Editor", label: "Editor" },
  { value: "User", raw: "contracts_User", label: "User" }
];
const EDITABLE_USER_ROLE_RAW_SET = new Set(USER_ROLE_EDIT_OPTIONS.map((roleOption) => roleOption.raw));
const AVAILABLE_API_SCOPES = [
  { value: "contacts:read", label: "Contacts · read" },
  { value: "contacts:write", label: "Contacts · write" },
  { value: "documents:read", label: "Documents · read" },
  { value: "documents:write", label: "Documents · write" }
];
const DEFAULT_API_TOKEN_FORM = {
  label: "",
  scopes: [],
  expiresAt: ""
};

const getRoleShortValue = (rawRole) => {
  const raw = String(rawRole || "").trim();
  if (!raw) {
    return "";
  }
  return raw.replace(/^contracts_/i, "");
};

const normalizeSignatureTypes = (signatureType) => {
  const source = Array.isArray(signatureType) && signatureType.length ? signatureType : DEFAULT_SIGNATURE_TYPES;
  const byName = source.reduce((acc, item) => {
    const rawName = item?.name;
    if (!rawName) {
      return acc;
    }
    const name = rawName === "type" ? "typed" : rawName;
    acc[name] = { name, enabled: item?.enabled !== false };
    return acc;
  }, {});

  return DEFAULT_SIGNATURE_TYPES.map((base) => {
    if (byName[base.name]) {
      return byName[base.name];
    }
    return { ...base };
  });
};

const generatePassword = (length = 12) => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i += 1) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const defaultAddUserForm = () => ({
  name: "",
  email: "",
  phone: "",
  role: "",
  password: generatePassword(12),
  sendOnboardingEmail: true
});

const defaultRequestTemplate = (appName, t) => ({
  subject: t("new-ui.settings.email.defaults.request.subject", {
    defaultValue: "{{sender_name}} requested your signature on {{document_title}}"
  }),
  body: t("new-ui.settings.email.defaults.request.body", {
    appName,
    defaultValue:
      "<p>Hello {{receiver_name}},</p><p>{{sender_name}} has requested your signature on <strong>{{document_title}}</strong>.</p><p>Please review and sign using this link:</p><p>{{signing_url}}</p><p>Thanks,<br/>{{appName}} team</p>"
  })
});

const defaultCompletionTemplate = (appName, t) => ({
  subject: t("new-ui.settings.email.defaults.completion.subject", {
    defaultValue: "{{document_title}} has been signed by all parties"
  }),
  body: t("new-ui.settings.email.defaults.completion.body", {
    appName,
    defaultValue:
      "<p>Hello {{sender_name}},</p><p>The document <strong>{{document_title}}</strong> has been signed by all parties.</p><p>You can download the signed file and completion certificate from {{appName}}.</p><p>Thanks,<br/>{{appName}} team</p>"
  })
});

const unwrapHtmlBody = (value) => {
  const raw = String(value || "").trim();
  if (!raw) {
    return "";
  }
  return raw
    .replace(/^<html[^>]*>\s*<head>[\s\S]*?<\/head>\s*<body>/i, "")
    .replace(/<\/body>\s*<\/html>$/i, "")
    .trim();
};

const formatDatePreview = (timezone, is12HourTime = false) => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: timezone || "UTC",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: is12HourTime
    }).format(new Date());
  } catch (error) {
    return new Date().toLocaleString();
  }
};

const normalizeSignatureName = (value) => {
  const cleaned = String(value || "")
    .replace(/\.[^./\\]+$/, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return cleaned || "signature";
};

const parseThreshold = (value, fallback) => {
  const parsedValue = parseInt(value, 10);
  return Number.isNaN(parsedValue) ? fallback : parsedValue;
};

const formatDateTimeValue = (value) => {
  if (!value) {
    return "—";
  }
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "—";
    }
    return date.toLocaleString();
  } catch (error) {
    return "—";
  }
};

const isExpiredDate = (value) => {
  if (!value) {
    return false;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return false;
  }
  return date.getTime() < Date.now();
};

const getApiTokenStatus = (token) => {
  if (token?.revoked === true) {
    return "revoked";
  }
  if (isExpiredDate(token?.expiresAt)) {
    return "expired";
  }
  return "active";
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result || "");
    reader.onerror = () => reject(new Error("file-read-failed"));
    reader.readAsDataURL(file);
  });

const getImageSize = (src) =>
  new Promise((resolve, reject) => {
    if (!src) {
      resolve({ width: 0, height: 0 });
      return;
    }
    const image = new Image();
    image.onload = () =>
      resolve({
        width: image.naturalWidth || image.width || 0,
        height: image.naturalHeight || image.height || 0
      });
    image.onerror = () => reject(new Error("image-load-failed"));
    image.src = src;
  });

const dataUrlToFile = (dataUrl, filenameBase) => {
  const parts = String(dataUrl || "").split(",");
  if (parts.length < 2) {
    return null;
  }
  const mimeMatch = parts[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/png";
  const binary = atob(parts[1]);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    array[i] = binary.charCodeAt(i);
  }
  const extension = mime.split("/").pop() || "png";
  return new File([array], `${filenameBase}.${extension}`, { type: mime });
};

const validateDrawnSignature = (canvasRef, minAreaThreshold, minLengthThreshold) => {
  if (!canvasRef?.current || typeof canvasRef.current.toData !== "function") {
    return false;
  }
  const strokes = canvasRef.current.toData();
  const points = [];
  strokes.forEach((stroke) => {
    (stroke.points || stroke || []).forEach((point) => points.push(point));
  });
  if (!points.length) {
    return false;
  }

  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const area = (maxX - minX) * (maxY - minY);

  let length = 0;
  strokes.forEach((stroke) => {
    const strokePoints = stroke.points || stroke || [];
    for (let i = 1; i < strokePoints.length; i += 1) {
      const dx = strokePoints[i].x - strokePoints[i - 1].x;
      const dy = strokePoints[i].y - strokePoints[i - 1].y;
      length += Math.sqrt(dx * dx + dy * dy);
    }
  });

  return area >= minAreaThreshold && length >= minLengthThreshold;
};

const validateImageSignature = (naturalWH, minAreaThreshold, minLengthThreshold) => {
  const area = (naturalWH?.width || 0) * (naturalWH?.height || 0);
  const longestSide = Math.max(naturalWH?.width || 0, naturalWH?.height || 0);
  return area >= minAreaThreshold && longestSide >= minLengthThreshold;
};

function InfoHint({ text = "" }) {
  const [isOpen, setIsOpen] = useState(false);

  if (!text) {
    return null;
  }

  return (
    <span
      className="relative ml-1 inline-flex"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        type="button"
        className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-sky-400 text-[11px] font-bold leading-none text-sky-500"
        aria-label={text}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
      >
        ?
      </button>
      {isOpen ? (
        <span className="pointer-events-none absolute left-1/2 top-[calc(100%+8px)] z-[1200] w-[260px] -translate-x-1/2 rounded-lg border border-app-line bg-white p-2 text-left text-xs font-medium text-app-ink shadow-soft">
          {text}
        </span>
      ) : null}
    </span>
  );
}

const readProfileState = () => {
  const extUser = getExtUser();
  const currentUser = Parse.User.current();
  const userInformation = parseStoredJson("UserInformation") || {};
  const tenantName =
    extUser?.TenantId?.TenantName || localStorage.getItem("TenantName") || extUser?.Company || "";

  return {
    name:
      extUser?.Name ||
      currentUser?.get("name") ||
      userInformation?.name ||
      localStorage.getItem("username") ||
      "",
    email:
      currentUser?.getEmail?.() ||
      localStorage.getItem("userEmail") ||
      userInformation?.email ||
      "",
    phone: extUser?.Phone || currentUser?.get("phone") || userInformation?.phone || "",
    company: String(tenantName || "").trim(),
    jobTitle: extUser?.JobTitle || "",
    language: String(
      extUser?.Language ||
        extUser?.language ||
        userInformation?.language ||
        userInformation?.Language ||
        localStorage.getItem("i18nextLng") ||
        "es"
    )
      .toLowerCase()
      .split("-")[0]
  };
};

const readPreferenceState = () => {
  const extUser = getExtUser();
  const tenant = extUser?.TenantId || {};
  return {
    notifyOnSignatures:
      extUser?.NotifyOnSignatures !== undefined ? Boolean(extUser.NotifyOnSignatures) : true,
    sendInOrder: extUser?.SendinOrder !== undefined ? Boolean(extUser.SendinOrder) : true,
    enableOtp: extUser?.IsEnableOTP !== undefined ? Boolean(extUser.IsEnableOTP) : false,
    isTourEnabled: extUser?.IsTourEnabled !== undefined ? Boolean(extUser.IsTourEnabled) : true,
    is12HourTime: extUser?.Is12HourTime !== undefined ? Boolean(extUser.Is12HourTime) : false,
    isLtvEnabled: true,
    automaticReminders: extUser?.AutomaticReminders !== undefined ? Boolean(extUser.AutomaticReminders) : false,
    remindOnceInEvery: String(extUser?.RemindOnceInEvery || 5),
    dateFormat: extUser?.DateFormat || "MM/DD/YYYY",
    defaultTimeToCompleteDays: String(Math.max(1, parseThreshold(extUser?.DefaultTimeToCompleteDays, 15))),
    minSignatureArea:
      tenant?.MinSignatureArea !== undefined
        ? String(tenant.MinSignatureArea)
        : extUser?.MinSignatureArea !== undefined
          ? String(extUser.MinSignatureArea)
        : String(DEFAULT_SIGNATURE_VALIDATION.minArea),
    minSignatureLength:
      tenant?.MinSignatureLength !== undefined
        ? String(tenant.MinSignatureLength)
        : extUser?.MinSignatureLength !== undefined
          ? String(extUser.MinSignatureLength)
        : String(DEFAULT_SIGNATURE_VALIDATION.minLength),
    signatureTypes: normalizeSignatureTypes(extUser?.SignatureType),
    timezone:
      extUser?.Timezone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      "America/Mexico_City"
  };
};

const readTenantSignatureValidationState = () => {
  const extUser = getExtUser();
  const tenant = extUser?.TenantId || {};
  return {
    minSignatureArea: String(
      parseThreshold(tenant?.MinSignatureArea ?? extUser?.MinSignatureArea, DEFAULT_SIGNATURE_VALIDATION.minArea)
    ),
    minSignatureLength: String(
      parseThreshold(
        tenant?.MinSignatureLength ?? extUser?.MinSignatureLength,
        DEFAULT_SIGNATURE_VALIDATION.minLength
      )
    )
  };
};

const MODE_META = {
  profile: {
    icon: "user",
    titleFallback: "Profile settings",
    descriptionFallback: "Update your personal and company information."
  },
  preferences: {
    icon: "sliders",
    titleFallback: "Preferences",
    descriptionFallback: "Configure global account settings."
  },
  "user-preferences": {
    icon: "sliders",
    titleFallback: "Preferences",
    descriptionFallback: "Configure your personal preferences."
  },
  password: {
    icon: "key",
    titleFallback: "Change password",
    descriptionFallback: "Update your account password securely."
  },
  users: {
    icon: "users",
    titleFallback: "Users",
    descriptionFallback: "Review users in your tenant."
  },
  signature: {
    icon: "signature",
    titleFallback: "My signature",
    descriptionFallback: "Create and update your saved signature and initials."
  },
  api: {
    icon: "settings",
    titleFallback: "API token",
    descriptionFallback: "Generate and manage API tokens for external integrations."
  },
  branding: {
    icon: "palette",
    titleFallback: "App Branding",
    descriptionFallback: "Customize the appearance of your application."
  }
};

function AccountSettingsPage({ mode = "profile" }) {
  const { t } = useTranslation();
  const { showError, showSuccess } = useAlert();
  const initialTenantBranding = useMemo(() => resolveTenantBrandingFromStorage(), []);
  const currentRole = useMemo(() => getExtUser()?.UserRole || "", []);
  const isAdminUser = currentRole === "contracts_Admin";
  const isOrgAdminUser = currentRole === "contracts_OrgAdmin";
  const isEditorUser = currentRole === "contracts_Editor";
  const isBasicUser = currentRole === "contracts_User";
  const canManageUsers = isAdminRole(currentRole);
  const canManageApiTokens = isAdminRole(currentRole);
  const canManageUserRoles = isAdminUser;
  const canManageTenantEmailTemplates = isAdminUser || isOrgAdminUser;
  const canManageTenantBranding = isAdminUser;
  const canManageTenantSignatureValidation = isAdminUser;
  const canManagePersonalEmailPermission = isAdminUser;
  const canUsePreferences = [isAdminUser, isOrgAdminUser, isEditorUser, isBasicUser].some(Boolean);

  const meta = MODE_META[mode] || MODE_META.profile;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!error) {
      return;
    }
    showError(error);
  }, [error, showError]);

  useEffect(() => {
    if (!success) {
      return;
    }
    showSuccess(success);
  }, [success, showSuccess]);

  const [profile, setProfile] = useState(() => readProfileState());
  const [preferences, setPreferences] = useState(() => readPreferenceState());
  const [tenantUsers, setTenantUsers] = useState([]);
  const [apiTokens, setApiTokens] = useState([]);
  const [showRevokedApiTokens, setShowRevokedApiTokens] = useState(false);
  const [isApiTokensLoading, setIsApiTokensLoading] = useState(false);
  const [isApiTokenSubmitting, setIsApiTokenSubmitting] = useState(false);
  const [isApiTokenCreateModalOpen, setIsApiTokenCreateModalOpen] = useState(false);
  const [revealedApiToken, setRevealedApiToken] = useState(null);
  const [apiTokenForm, setApiTokenForm] = useState(() => ({ ...DEFAULT_API_TOKEN_FORM }));
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [isAddUserLoading, setIsAddUserLoading] = useState(false);
  const [addUserForm, setAddUserForm] = useState(() => defaultAddUserForm());
  const [isToggleUserModalOpen, setIsToggleUserModalOpen] = useState(false);
  const [isToggleUserLoading, setIsToggleUserLoading] = useState(false);
  const [userPendingStatusChange, setUserPendingStatusChange] = useState(null);
  const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false);
  const [isDeleteUserLoading, setIsDeleteUserLoading] = useState(false);
  const [userPendingDelete, setUserPendingDelete] = useState(null);
  const [userRoleDraftById, setUserRoleDraftById] = useState({});
  const [isRoleUpdateLoadingById, setIsRoleUpdateLoadingById] = useState({});
  const [allowPersonalEmailTemplatesForAdmin, setAllowPersonalEmailTemplatesForAdmin] = useState(() => {
    const resolvedPermissions = resolveTenantPersonalEmailPermissions(getExtUser()?.TenantId || {});
    return resolvedPermissions.allowAdmin;
  });
  const [allowPersonalEmailTemplatesForEditor, setAllowPersonalEmailTemplatesForEditor] = useState(() => {
    const resolvedPermissions = resolveTenantPersonalEmailPermissions(getExtUser()?.TenantId || {});
    return resolvedPermissions.allowEditor;
  });
  const [allowPersonalEmailTemplatesForUser, setAllowPersonalEmailTemplatesForUser] = useState(() => {
    const resolvedPermissions = resolveTenantPersonalEmailPermissions(getExtUser()?.TenantId || {});
    return resolvedPermissions.allowUser;
  });
  const [preferencesTab, setPreferencesTab] = useState(() => {
    if (mode === "preferences") {
      if (canManageTenantSignatureValidation) {
        return "configuration";
      }
      if (canManageTenantEmailTemplates) {
        return "email";
      }
      return "configuration";
    }
    return "general";
  });
  const [tenantId, setTenantId] = useState("");
  const [tenantLogoUrl, setTenantLogoUrl] = useState(initialTenantBranding.logo);
  const [tenantLogoPreview, setTenantLogoPreview] = useState(initialTenantBranding.logo);
  const [tenantLogoFile, setTenantLogoFile] = useState(null);
  const [tenantPrimaryColor, setTenantPrimaryColor] = useState(initialTenantBranding.color);
  const [tenantSignatureValidation, setTenantSignatureValidation] = useState(() =>
    readTenantSignatureValidationState()
  );
  const [requestSubject, setRequestSubject] = useState("");
  const [requestBody, setRequestBody] = useState("");
  const [requestBodyMode, setRequestBodyMode] = useState(EMAIL_TEMPLATE_MODE.HTML);
  const [completionSubject, setCompletionSubject] = useState("");
  const [completionBody, setCompletionBody] = useState("");
  const [completionBodyMode, setCompletionBodyMode] = useState(EMAIL_TEMPLATE_MODE.HTML);
  const [requestBodyHtmlDraft, setRequestBodyHtmlDraft] = useState("");
  const [completionBodyHtmlDraft, setCompletionBodyHtmlDraft] = useState("");
  const [personalRequestSubject, setPersonalRequestSubject] = useState("");
  const [personalRequestBody, setPersonalRequestBody] = useState("");
  const [personalRequestBodyMode, setPersonalRequestBodyMode] = useState(EMAIL_TEMPLATE_MODE.HTML);
  const [personalRequestBodyHtmlDraft, setPersonalRequestBodyHtmlDraft] = useState("");
  const [usePersonalRequestEmailByDefault, setUsePersonalRequestEmailByDefault] = useState(false);
  const [isEmailPreviewOpen, setIsEmailPreviewOpen] = useState(false);
  const [emailPreviewSubject, setEmailPreviewSubject] = useState("");
  const [emailPreviewHtml, setEmailPreviewHtml] = useState("");
  const [emailPreviewIsPlain, setEmailPreviewIsPlain] = useState(false);
  const [isTestEmailModalOpen, setIsTestEmailModalOpen] = useState(false);
  const [testEmailRecipient, setTestEmailRecipient] = useState("");
  const [testEmailType, setTestEmailType] = useState("request");
  const [isSendingTestEmail, setIsSendingTestEmail] = useState(false);
  const requestEmailEditorRef = useRef(null);
  const completionEmailEditorRef = useRef(null);
  const personalRequestEmailEditorRef = useRef(null);
  const requestPlainBodyRef = useRef(null);
  const completionPlainBodyRef = useRef(null);
  const personalRequestPlainBodyRef = useRef(null);
  const tenantLogoInputRef = useRef(null);

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [penColor, setPenColor] = useState(SIGNATURE_COLOR_OPTIONS[0]);
  const [initialsPenColor, setInitialsPenColor] = useState(SIGNATURE_COLOR_OPTIONS[0]);
  const [signatureRecordId, setSignatureRecordId] = useState("");
  const [signatureName, setSignatureName] = useState("");
  const [signatureImage, setSignatureImage] = useState("");
  const [signedSignatureUrl, setSignedSignatureUrl] = useState("");
  const [signatureDataUrl, setSignatureDataUrl] = useState("");
  const [signatureNaturalWH, setSignatureNaturalWH] = useState({});
  const [initialsImage, setInitialsImage] = useState("");
  const [signedInitialsUrl, setSignedInitialsUrl] = useState("");
  const [initialsDataUrl, setInitialsDataUrl] = useState("");
  const [initialsNaturalWH, setInitialsNaturalWH] = useState({});
  const [initialsText, setInitialsText] = useState("");
  const [initialsMode, setInitialsMode] = useState("draw");
  const [signatureCanvasWidth, setSignatureCanvasWidth] = useState(640);
  const [initialsCanvasWidth, setInitialsCanvasWidth] = useState(420);
  const signatureCanvasRef = useRef(null);
  const initialsCanvasRef = useRef(null);
  const signatureUploadRef = useRef(null);
  const initialsUploadRef = useRef(null);
  const signatureCanvasHostRef = useRef(null);
  const initialsCanvasHostRef = useRef(null);

  const modeTitle = useMemo(() => {
    if (mode === "profile") return t("profile", { defaultValue: meta.titleFallback });
    if (mode === "preferences")
      return t("new-ui.settings.global-configuration-label", { defaultValue: "Configuration" });
    if (mode === "user-preferences")
      return t("sidebar.Settings-Children.Preferences", { defaultValue: meta.titleFallback });
    if (mode === "password") return t("change-password", { defaultValue: meta.titleFallback });
    if (mode === "users") return t("sidebar.Settings-Children.Users", { defaultValue: meta.titleFallback });
    if (mode === "signature") return t("sidebar.Settings-Children.My Signature", { defaultValue: meta.titleFallback });
    if (mode === "api") return t("sidebar.Settings-Children.API Token", { defaultValue: meta.titleFallback });
    if (mode === "branding") return t("new-ui.settings.app-branding.title", { defaultValue: meta.titleFallback });
    return meta.titleFallback;
  }, [mode, meta.titleFallback, t]);
  const modeDescription = useMemo(() => {
    if (mode === "preferences") {
      return t("new-ui.settings.mode-description.preferences", {
        defaultValue:
          "Configure global account settings. Changes in this section apply to all users in your account."
      });
    }
    if (mode === "user-preferences") {
      return t("new-ui.settings.mode-description.user-preferences", {
        defaultValue:
          "Configure your personal preferences. Changes in this section apply only to your user."
      });
    }
    return meta.descriptionFallback;
  }, [mode, meta.descriptionFallback, t]);
  const tentan = useMemo(() => {
    const extUser = getExtUser();
    const tenantName =
      extUser?.TenantId?.TenantName || localStorage.getItem("TenantName") || extUser?.Company || "tenant";
    return String(tenantName || "tenant").trim();
  }, []);
  const modeKicker = useMemo(() => {
    const isTenantScope = mode === "preferences" || mode === "users" || mode === "branding";
    if (isTenantScope) {
      return t("new-ui.sidebar.tenant-account-configuration", {
        tentan,
        defaultValue: "Global account configuration"
      });
    }
    return t("new-ui.settings.mode-kicker.personal", {
      defaultValue: "Personal account settings"
    });
  }, [mode, tentan, t]);
  const canAccessPreferencesPanel =
    mode === "preferences"
      ? canManageTenantEmailTemplates || canManageTenantSignatureValidation
      : canUsePreferences;
  const filteredApiTokens = useMemo(() => {
    if (showRevokedApiTokens) {
      return apiTokens;
    }
    return apiTokens.filter((token) => token?.revoked !== true);
  }, [apiTokens, showRevokedApiTokens]);
  const canUsePersonalEmailTemplates = canUsePersonalEmailTemplatesByRole(currentRole, {
    allowAdmin: allowPersonalEmailTemplatesForAdmin,
    allowEditor: allowPersonalEmailTemplatesForEditor,
    allowUser: allowPersonalEmailTemplatesForUser
  });
  const isPersonalRequestEditorEnabled = usePersonalRequestEmailByDefault === true;
  const fallbackRequestTemplate = useMemo(() => defaultRequestTemplate("DANASign", t), [t]);
  const corporateRequestSubjectDisplay =
    String(requestSubject || "").trim() || fallbackRequestTemplate.subject;
  const corporateRequestBodyModeDisplay = normalizeEmailTemplateMode(requestBodyMode);
  const corporateRequestBodyDisplay =
    String(requestBody || "").trim() ||
    (corporateRequestBodyModeDisplay === EMAIL_TEMPLATE_MODE.PLAIN
      ? htmlToPlainText(fallbackRequestTemplate.body)
      : fallbackRequestTemplate.body);
  const currentExtUserObjectId = useMemo(() => String(getExtUser()?.objectId || "").trim(), []);
  const emailShellCopy = useMemo(
    () => ({
      requestTitle: t("new-ui.settings.email.shell-request-title", {
        defaultValue: "Digital Signature Request"
      }),
      completionTitle: t("new-ui.settings.email.shell-completion-title", {
        defaultValue: "Document Completion"
      }),
      footer: t("new-ui.settings.email.shell-footer", {
        appName: "DANASign",
        defaultValue:
          "This is an automated email from {{appName}}. For questions, contact the sender directly at {{sender_mail}}."
      })
    }),
    [t]
  );
  const emailBranding = useMemo(
    () => ({
      logoUrl: tenantLogoUrl || DEFAULT_TENANT_LOGO_URL,
      themeColor: normalizeHexColor(tenantPrimaryColor)
    }),
    [tenantLogoUrl, tenantPrimaryColor]
  );
  const resetApiTokenForm = () => {
    setApiTokenForm({ ...DEFAULT_API_TOKEN_FORM });
  };

  const fetchApiTokens = async () => {
    const response = await Parse.Cloud.run("listapitokens", {
      includeRevoked: true
    });
    return normalizeCloudArray(response);
  };

  useEffect(() => {
    let cancelled = false;

    const loadUsers = async () => {
      if (mode !== "users") {
        return;
      }

      setIsLoading(true);
      setError("");
      try {
        let extUser = getExtUser();
        if (!extUser?.OrganizationId?.objectId) {
          const cloudUser = await Parse.Cloud.run("getUserDetails");
          const extFromCloud = typeof cloudUser?.toJSON === "function" ? cloudUser.toJSON() : cloudUser;
          if (extFromCloud) {
            extUser = extFromCloud;
            localStorage.setItem("Extand_Class", JSON.stringify([extFromCloud]));
            if (extFromCloud?.TenantId?.objectId) {
              localStorage.setItem("TenantId", extFromCloud.TenantId.objectId);
            }
          }
        }

        if (!isAdminRole(extUser?.UserRole)) {
          throw new Error(
            t("something-went-wrong-mssg", {
              defaultValue: "You do not have permission to view users."
            })
          );
        }

        let organizationId = extUser?.OrganizationId?.objectId;
        
        // Fallback: if OrganizationId is missing, try to get it from TenantId
        if (!organizationId && extUser?.TenantId?.objectId) {
          // For OrgAdmin users, the organization might be stored differently
          // Try to fetch the tenant to get organization info
          try {
            const tenantResponse = await Parse.Cloud.run("gettenant", { userId: Parse.User.current()?.id });
            const tenant = typeof tenantResponse?.toJSON === "function" ? tenantResponse.toJSON() : tenantResponse;
            organizationId = tenant?.OrganizationId?.objectId || tenant?.objectId;
          } catch (tenantError) {
            console.warn("Could not fetch tenant for organization ID", tenantError);
          }
        }
        
        if (!organizationId) {
          throw new Error(
            t("new-ui.settings.users.organization-missing", {
              defaultValue: "Organization context missing. Please sign in again."
            })
          );
        }

        const usersResponse = await Parse.Cloud.run("getuserlistbyorg", {
          organizationId
        });
        const users = normalizeCloudArray(usersResponse);

        if (!cancelled) {
          const normalizedUsers = users.map((user) => ({
            objectId: user?.objectId || user?.id || "",
            name: user?.Name || "-",
            email: user?.Email || "-",
            rawRole: user?.UserRole || "",
            role: formatUserRole(user?.UserRole, t),
            active: user?.IsDisabled !== true
          }));
          setTenantUsers(normalizedUsers);
          setUserRoleDraftById(
            normalizedUsers.reduce((acc, user) => {
              acc[user.objectId] = getRoleShortValue(user.rawRole);
              return acc;
            }, {})
          );
        }
      } catch (usersError) {
        console.error("Failed to load tenant users", usersError);
        if (!cancelled) {
          const message =
            usersError?.message ||
            t("new-ui.dashboard.errors.load-failed", {
              defaultValue: "Could not load users from backend."
            });
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadUsers();
    return () => {
      cancelled = true;
    };
  }, [mode, t]);

  useEffect(() => {
    let cancelled = false;

    const loadApiTokens = async () => {
      if (mode !== "api") {
        return;
      }

      if (!canManageApiTokens) {
        setApiTokens([]);
        return;
      }

      setIsApiTokensLoading(true);
      setError("");
      try {
        const tokens = await fetchApiTokens();
        if (!cancelled) {
          setApiTokens(tokens);
        }
      } catch (apiTokenError) {
        console.error("Failed to load API tokens", apiTokenError);
        if (!cancelled) {
          setError(
            apiTokenError?.message ||
              t("new-ui.settings.api.error-loading", {
                defaultValue: "Could not load API tokens."
              })
          );
        }
      } finally {
        if (!cancelled) {
          setIsApiTokensLoading(false);
        }
      }
    };

    loadApiTokens();
    return () => {
      cancelled = true;
    };
  }, [mode, canManageApiTokens, t]);

  useEffect(() => {
    let cancelled = false;

    const loadTenantEmailPermission = async () => {
      if (mode !== "preferences" && mode !== "user-preferences") {
        return;
      }

      try {
        const userId = Parse.User.current()?.id;
        const tenantResponse = await Parse.Cloud.run("gettenant", { userId });
        const tenant = typeof tenantResponse?.toJSON === "function" ? tenantResponse.toJSON() : tenantResponse;
        if (cancelled) {
          return;
        }

        const resolvedPermissions = resolveTenantPersonalEmailPermissions(tenant || {});
        setAllowPersonalEmailTemplatesForAdmin(resolvedPermissions.allowAdmin);
        setAllowPersonalEmailTemplatesForEditor(resolvedPermissions.allowEditor);
        setAllowPersonalEmailTemplatesForUser(resolvedPermissions.allowUser);

        const extUser = getExtUser();
        const combinedLegacyPermission = resolvedPermissions.allowAdmin || resolvedPermissions.allowEditor;
        const nextExtUser = {
          ...extUser,
          TenantId: {
            ...(extUser?.TenantId || {}),
            ...(tenant || {}),
            AllowPersonalEmailTemplatesForAdmin: resolvedPermissions.allowAdmin,
            AllowPersonalEmailTemplatesForEditor: resolvedPermissions.allowEditor,
            AllowPersonalEmailTemplatesForUser: resolvedPermissions.allowUser,
            AllowPersonalEmailTemplatesForEditorOrgAdmin: combinedLegacyPermission
          }
        };
        localStorage.setItem("Extand_Class", JSON.stringify([nextExtUser]));
      } catch (tenantPermissionError) {
        console.error("Failed to load tenant personal email permission", tenantPermissionError);
      }
    };

    loadTenantEmailPermission();
    return () => {
      cancelled = true;
    };
  }, [mode]);

  useEffect(() => {
    let cancelled = false;

    const loadEmailTemplates = async () => {
      if (mode !== "preferences" && mode !== "user-preferences") {
        return;
      }
      if (mode === "preferences" && !canManageTenantEmailTemplates) {
        return;
      }

      try {
        const appName = "DANASign";
        const reqDefaults = defaultRequestTemplate(appName, t);
        const compDefaults = defaultCompletionTemplate(appName, t);
        const userId = Parse.User.current()?.id;
        const tenantResponse = await Parse.Cloud.run("gettenant", { userId });
        const tenant = typeof tenantResponse?.toJSON === "function" ? tenantResponse.toJSON() : tenantResponse;

        if (cancelled) {
          return;
        }

        const loadedRequestMode = normalizeEmailTemplateMode(tenant?.RequestBodyMode);
        const loadedCompletionMode = normalizeEmailTemplateMode(tenant?.CompletionBodyMode);
        const requestBodySource = extractTemplateContent(tenant?.RequestBody);
        const completionBodySource = extractTemplateContent(tenant?.CompletionBody);
        const requestBodyDefault =
          loadedRequestMode === EMAIL_TEMPLATE_MODE.PLAIN
            ? htmlToPlainText(reqDefaults.body)
            : reqDefaults.body;
        const completionBodyDefault =
          loadedCompletionMode === EMAIL_TEMPLATE_MODE.PLAIN
            ? htmlToPlainText(compDefaults.body)
            : compDefaults.body;

        setTenantId(tenant?.objectId || getExtUser()?.TenantId?.objectId || "");
        setRequestSubject(tenant?.RequestSubject || reqDefaults.subject);
        setRequestBodyMode(loadedRequestMode);
        setRequestBodyHtmlDraft(requestBodySource || reqDefaults.body || "");
        setRequestBody(
          requestBodySource
            ? loadedRequestMode === EMAIL_TEMPLATE_MODE.PLAIN
              ? htmlToPlainText(requestBodySource)
              : requestBodySource
            : requestBodyDefault
        );
        setCompletionSubject(tenant?.CompletionSubject || compDefaults.subject);
        setCompletionBodyMode(loadedCompletionMode);
        setCompletionBodyHtmlDraft(completionBodySource || compDefaults.body || "");
        setCompletionBody(
          completionBodySource
            ? loadedCompletionMode === EMAIL_TEMPLATE_MODE.PLAIN
              ? htmlToPlainText(completionBodySource)
              : completionBodySource
            : completionBodyDefault
        );
      } catch (templateError) {
        console.error("Failed to load tenant email templates", templateError);
      }
    };

    loadEmailTemplates();
    return () => {
      cancelled = true;
    };
  }, [mode, canManageTenantEmailTemplates, t]);

  useEffect(() => {
    if (mode !== "user-preferences") {
      return;
    }

    const appName = "DANASign";
    const reqDefaults = defaultRequestTemplate(appName, t);
    const extUser = getExtUser();
    const loadedMode = normalizeEmailTemplateMode(extUser?.PersonalRequestBodyMode);
    const requestBodySource = extractTemplateContent(extUser?.PersonalRequestBody);
    const requestBodyDefault =
      loadedMode === EMAIL_TEMPLATE_MODE.PLAIN ? htmlToPlainText(reqDefaults.body) : reqDefaults.body;

    setPersonalRequestSubject(extUser?.PersonalRequestSubject || reqDefaults.subject);
    setPersonalRequestBodyMode(loadedMode);
    setPersonalRequestBodyHtmlDraft(requestBodySource || reqDefaults.body || "");
    setPersonalRequestBody(
      requestBodySource
        ? loadedMode === EMAIL_TEMPLATE_MODE.PLAIN
          ? htmlToPlainText(requestBodySource)
          : requestBodySource
        : requestBodyDefault
    );
    setUsePersonalRequestEmailByDefault(Boolean(extUser?.UsePersonalRequestEmailByDefault));
  }, [mode, t]);

  useEffect(() => {
    if (requestBodyMode === EMAIL_TEMPLATE_MODE.HTML) {
      setRequestBodyHtmlDraft(requestBody || "");
    }
  }, [requestBody, requestBodyMode]);

  useEffect(() => {
    if (completionBodyMode === EMAIL_TEMPLATE_MODE.HTML) {
      setCompletionBodyHtmlDraft(completionBody || "");
    }
  }, [completionBody, completionBodyMode]);

  useEffect(() => {
    if (personalRequestBodyMode === EMAIL_TEMPLATE_MODE.HTML) {
      setPersonalRequestBodyHtmlDraft(personalRequestBody || "");
    }
  }, [personalRequestBody, personalRequestBodyMode]);

  useEffect(() => {
    let cancelled = false;

    const loadTenantBranding = async () => {
      if (mode !== "preferences" || (!canManageTenantBranding && !canManageTenantSignatureValidation)) {
        return;
      }

      try {
        const userId = Parse.User.current()?.id;
        const tenantResponse = await Parse.Cloud.run("gettenant", { userId });
        const tenant = typeof tenantResponse?.toJSON === "function" ? tenantResponse.toJSON() : tenantResponse;
        if (cancelled) {
          return;
        }

        const fallbackTenantId =
          getExtUser()?.TenantId?.objectId || localStorage.getItem("TenantId") || "";
        const nextTenantId = tenant?.objectId || fallbackTenantId;
        const nextLogo = String(tenant?.Logo || initialTenantBranding.logo || DEFAULT_TENANT_LOGO_URL).trim();
        const nextColor = normalizeHexColor(
          tenant?.PrimaryColor ||
            tenant?.primaryColor ||
            tenant?.ThemeColor ||
            tenant?.themeColor ||
            initialTenantBranding.color
        );
        const nextMinSignatureArea = parseThreshold(
          tenant?.MinSignatureArea ?? getExtUser()?.TenantId?.MinSignatureArea ?? getExtUser()?.MinSignatureArea,
          DEFAULT_SIGNATURE_VALIDATION.minArea
        );
        const nextMinSignatureLength = parseThreshold(
          tenant?.MinSignatureLength ??
            getExtUser()?.TenantId?.MinSignatureLength ??
            getExtUser()?.MinSignatureLength,
          DEFAULT_SIGNATURE_VALIDATION.minLength
        );

        setTenantId(nextTenantId);
        setTenantLogoUrl(nextLogo);
        setTenantLogoPreview(nextLogo);
        setTenantPrimaryColor(nextColor);
        setTenantSignatureValidation({
          minSignatureArea: String(nextMinSignatureArea),
          minSignatureLength: String(nextMinSignatureLength)
        });
        // applyTenantBrandColor(nextColor); // Commented out - tenant color should only affect emails, not UI
      } catch (tenantError) {
        console.error("Failed to load tenant branding", tenantError);
      }
    };

    loadTenantBranding();
    return () => {
      cancelled = true;
    };
  }, [mode, canManageTenantBranding, canManageTenantSignatureValidation, initialTenantBranding]);

  useEffect(() => {
    if (mode === "preferences") {
      const allowedTabs = [];
      if (canManageTenantSignatureValidation) {
        allowedTabs.push("configuration");
      }
      if (canManageTenantEmailTemplates) {
        allowedTabs.push("email");
      }
      if (!allowedTabs.length) {
        if (preferencesTab !== "configuration") {
          setPreferencesTab("configuration");
        }
        return;
      }
      if (!allowedTabs.includes(preferencesTab)) {
        setPreferencesTab(allowedTabs[0]);
      }
      return;
    }
    if (mode === "user-preferences") {
      if (preferencesTab !== "general" && preferencesTab !== "email") {
        setPreferencesTab("general");
        return;
      }
      if (!canUsePersonalEmailTemplates && preferencesTab === "email") {
        setPreferencesTab("general");
      }
    }
  }, [
    mode,
    canManageTenantEmailTemplates,
    canManageTenantSignatureValidation,
    canUsePersonalEmailTemplates,
    preferencesTab
  ]);

  // Commented out - tenant color should only affect emails, not UI
  // useEffect(() => {
  //   applyTenantBrandColor(tenantPrimaryColor);
  // }, [tenantPrimaryColor]);

  useEffect(() => {
    let cancelled = false;

    const loadSignature = async () => {
      if (mode !== "signature") {
        return;
      }

      setIsLoading(true);
      setError("");
      setSuccess("");

      try {
        const currentUser = Parse.User.current();
        if (!currentUser?.id) {
          throw new Error(t("something-went-wrong-mssg", { defaultValue: "Something went wrong." }));
        }

        const userPointer = {
          __type: "Pointer",
          className: "_User",
          objectId: currentUser.id
        };

        const signQuery = new Parse.Query("contracts_Signature");
        signQuery.equalTo("UserId", userPointer);
        const signRecord = await signQuery.first();

        if (cancelled) {
          return;
        }

        const fallbackName = normalizeSignatureName(
          currentUser.get("name") || profile.name || localStorage.getItem("username") || ""
        );

        if (signRecord) {
          const signatureData = signRecord.toJSON();
          setSignatureRecordId(signatureData?.objectId || signRecord.id || "");
          setSignatureName(normalizeSignatureName(signatureData?.SignatureName || fallbackName));
          setSignatureImage(signatureData?.ImageURL || "");
          setInitialsImage(signatureData?.Initials || "");
        } else {
          setSignatureRecordId("");
          setSignatureName(fallbackName);
          setSignatureImage("");
          setInitialsImage("");
        }

        setSignatureDataUrl("");
        setInitialsDataUrl("");
        setSignatureNaturalWH({});
        setInitialsNaturalWH({});
        signatureCanvasRef.current?.clear?.();
        initialsCanvasRef.current?.clear?.();
      } catch (signatureError) {
        console.error("Failed to load signature", signatureError);
        if (!cancelled) {
          setError(
            signatureError?.message || t("something-went-wrong-mssg", { defaultValue: "Something went wrong." })
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadSignature();
    return () => {
      cancelled = true;
    };
  }, [mode, profile.name, t]);

  useEffect(() => {
    if (mode !== "signature") {
      return undefined;
    }

    const syncCanvasSize = () => {
      const signatureHostWidth = Math.max(
        MIN_CANVAS_WIDTH,
        Math.round(signatureCanvasHostRef.current?.clientWidth || 0)
      );
      const initialsHostWidth = Math.max(
        MIN_CANVAS_WIDTH,
        Math.round(initialsCanvasHostRef.current?.clientWidth || 0)
      );

      setSignatureCanvasWidth((current) =>
        current === signatureHostWidth ? current : signatureHostWidth
      );
      setInitialsCanvasWidth((current) =>
        current === initialsHostWidth ? current : initialsHostWidth
      );
    };

    syncCanvasSize();
    window.addEventListener("resize", syncCanvasSize);

    let resizeObserver;
    if (typeof ResizeObserver === "function") {
      resizeObserver = new ResizeObserver(syncCanvasSize);
      if (signatureCanvasHostRef.current) {
        resizeObserver.observe(signatureCanvasHostRef.current);
      }
      if (initialsCanvasHostRef.current) {
        resizeObserver.observe(initialsCanvasHostRef.current);
      }
    }

    return () => {
      window.removeEventListener("resize", syncCanvasSize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [mode]);

  useEffect(() => {
    if (mode !== "signature" || !signatureImage) {
      return;
    }

    let cancelled = false;

    const signUrls = async () => {
      try {
        const signed = await getSignedSignatureUrl(signatureImage);
        if (!cancelled) {
          setSignedSignatureUrl(signed);
        }
      } catch (err) {
        console.error("Failed to sign signature URL", err);
        if (!cancelled) {
          setSignedSignatureUrl(signatureImage);
        }
      }
    };

    signUrls();
    return () => {
      cancelled = true;
    };
  }, [signatureImage, mode]);

  useEffect(() => {
    if (mode !== "signature" || !initialsImage) {
      return;
    }

    let cancelled = false;

    const signUrls = async () => {
      try {
        const signed = await getSignedSignatureUrl(initialsImage);
        if (!cancelled) {
          setSignedInitialsUrl(signed);
        }
      } catch (err) {
        console.error("Failed to sign initials URL", err);
        if (!cancelled) {
          setSignedInitialsUrl(initialsImage);
        }
      }
    };

    signUrls();
    return () => {
      cancelled = true;
    };
  }, [initialsImage, mode]);

  const timezoneOptions = useMemo(() => {
    const browserTimezones =
      typeof Intl.supportedValuesOf === "function"
        ? Intl.supportedValuesOf("timeZone")
        : [
            "America/New_York",
            "America/Chicago",
            "America/Denver",
            "America/Los_Angeles",
            "America/Mexico_City",
            "UTC"
          ];

    const options = Array.from(new Set([preferences.timezone, ...browserTimezones].filter(Boolean)));
    return options.slice(0, 500);
  }, [preferences.timezone]);

  const openCreateApiTokenModal = () => {
    resetApiTokenForm();
    setRevealedApiToken(null);
    setIsApiTokenCreateModalOpen(true);
  };

  const closeCreateApiTokenModal = () => {
    if (isApiTokenSubmitting) {
      return;
    }
    setIsApiTokenCreateModalOpen(false);
    resetApiTokenForm();
  };

  const toggleApiTokenScope = (scopeValue) => {
    setApiTokenForm((current) => {
      const scopes = new Set(current.scopes);
      if (scopes.has(scopeValue)) {
        scopes.delete(scopeValue);
      } else {
        scopes.add(scopeValue);
      }
      return {
        ...current,
        scopes: Array.from(scopes)
      };
    });
  };

  const handleCreateApiToken = async (event) => {
    event.preventDefault();
    if (!canManageApiTokens || isApiTokenSubmitting) {
      return;
    }

    setIsApiTokenSubmitting(true);
    setError("");
    setSuccess("");

    try {
      let expiresAt = null;
      const rawExpiresAt = String(apiTokenForm.expiresAt || "").trim();
      if (rawExpiresAt) {
        const parsedDate = new Date(rawExpiresAt);
        if (Number.isNaN(parsedDate.getTime())) {
          throw new Error(
            t("new-ui.settings.api.invalid-expiration", {
              defaultValue: "Invalid expiration date."
            })
          );
        }
        expiresAt = parsedDate.toISOString();
      }

      const response = await Parse.Cloud.run("createapitoken", {
        label: String(apiTokenForm.label || "").trim(),
        scopes: Array.isArray(apiTokenForm.scopes) ? apiTokenForm.scopes : [],
        expiresAt
      });
      const refreshedTokens = await fetchApiTokens();

      setApiTokens(refreshedTokens);
      setRevealedApiToken(response || null);
      setSuccess(
        t("new-ui.settings.api.token-created", {
          defaultValue: "New token created. Save it securely because it will not be shown again."
        })
      );
      setIsApiTokenCreateModalOpen(false);
      resetApiTokenForm();
    } catch (createTokenError) {
      console.error("Failed to create API token", createTokenError);
      setError(
        createTokenError?.message ||
          t("new-ui.settings.api.error-create", {
            defaultValue: "Could not create API token."
          })
      );
    } finally {
      setIsApiTokenSubmitting(false);
    }
  };

  const handleRevokeApiToken = async (tokenId) => {
    if (!canManageApiTokens || !tokenId) {
      return;
    }

    const confirmed = window.confirm(
      t("new-ui.settings.api.revoke-confirm", {
        defaultValue:
          "This action will revoke the token and it will no longer be usable. Do you want to continue?"
      })
    );
    if (!confirmed) {
      return;
    }

    setIsApiTokenSubmitting(true);
    setError("");
    setSuccess("");
    try {
      const revokeResponse = await Parse.Cloud.run("revokeapitoken", { tokenId });
      if (revokeResponse?.revoked) {
        const refreshedTokens = await fetchApiTokens();
        setApiTokens(refreshedTokens);
        setSuccess(
          t("new-ui.settings.api.token-revoked", {
            defaultValue: "Token revoked successfully."
          })
        );
      }
    } catch (revokeTokenError) {
      console.error("Failed to revoke API token", revokeTokenError);
      setError(
        revokeTokenError?.message ||
          t("new-ui.settings.api.error-revoke", {
            defaultValue: "Could not revoke API token."
          })
      );
    } finally {
      setIsApiTokenSubmitting(false);
    }
  };

  const handleCopyApiToken = async (tokenValue) => {
    if (!tokenValue) {
      return;
    }
    try {
      await navigator.clipboard.writeText(tokenValue);
      setSuccess(
        t("new-ui.settings.api.token-copied", {
          defaultValue: "Token copied to clipboard."
        })
      );
    } catch (clipboardError) {
      console.error("Failed to copy API token", clipboardError);
      setError(
        t("new-ui.settings.api.error-copy", {
          defaultValue: "Could not copy token."
        })
      );
    }
  };

  const openAddUserModal = async () => {
    if (!canManageUsers) {
      return;
    }

    setIsAddUserOpen(true);
    setAddUserForm(defaultAddUserForm());
  };

  const closeAddUserModal = () => {
    setIsAddUserOpen(false);
    setIsAddUserLoading(false);
    setAddUserForm(defaultAddUserForm());
  };

  const canEditUserStatus = (user) =>
    Boolean(user?.objectId) &&
    String(user?.rawRole || "") !== "contracts_Admin" &&
    String(user?.objectId || "") !== currentExtUserObjectId;

  const canEditUserRole = (user) =>
    Boolean(canManageUserRoles) &&
    Boolean(user?.objectId) &&
    String(user?.objectId || "") !== currentExtUserObjectId &&
    EDITABLE_USER_ROLE_RAW_SET.has(String(user?.rawRole || "").trim());

  const handleUserRoleDraftChange = (userId, nextRole) => {
    setUserRoleDraftById((current) => ({
      ...current,
      [userId]: nextRole
    }));
  };

  const handleSaveUserRole = async (user) => {
    if (!canEditUserRole(user) || !user?.objectId) {
      return;
    }

    const draftRole = String(userRoleDraftById[user.objectId] || "").trim();
    const fallbackRole = getRoleShortValue(user.rawRole);
    const nextRole = draftRole || fallbackRole;
    const nextRawRole = `contracts_${nextRole}`;

    if (!nextRole || nextRawRole === String(user.rawRole || "").trim()) {
      return;
    }

    setIsRoleUpdateLoadingById((current) => ({
      ...current,
      [user.objectId]: true
    }));
    setError("");
    setSuccess("");

    try {
      await Parse.Cloud.run("updateorguserrole", {
        userId: user.objectId,
        role: nextRole
      });

      setTenantUsers((current) =>
        current.map((currentUser) =>
          currentUser.objectId === user.objectId
            ? {
                ...currentUser,
                rawRole: nextRawRole,
                role: formatUserRole(nextRawRole, t)
              }
            : currentUser
        )
      );

      setSuccess(t("saved-successfully", { defaultValue: "Saved successfully." }));
    } catch (roleError) {
      console.error("Failed to update user role", roleError);
      setError(roleError?.message || t("something-went-wrong-mssg", { defaultValue: "Something went wrong." }));
      setUserRoleDraftById((current) => ({
        ...current,
        [user.objectId]: getRoleShortValue(user.rawRole)
      }));
    } finally {
      setIsRoleUpdateLoadingById((current) => ({
        ...current,
        [user.objectId]: false
      }));
    }
  };

  const openToggleUserModal = (user) => {
    if (!canManageUsers || !canEditUserStatus(user)) {
      return;
    }

    setUserPendingStatusChange(user);
    setIsToggleUserModalOpen(true);
  };

  const closeToggleUserModal = () => {
    if (isToggleUserLoading) {
      return;
    }
    setIsToggleUserModalOpen(false);
    setUserPendingStatusChange(null);
  };

  const handleConfirmUserStatusToggle = async () => {
    if (!canManageUsers || !userPendingStatusChange?.objectId) {
      return;
    }

    setIsToggleUserLoading(true);
    setError("");
    setSuccess("");

    try {
      const nextIsDisabled = userPendingStatusChange.active === true;
      const extUser = new Parse.Object("contracts_Users");
      extUser.id = userPendingStatusChange.objectId;
      extUser.set("IsDisabled", nextIsDisabled);
      await extUser.save();

      setTenantUsers((current) =>
        current.map((user) =>
          user.objectId === userPendingStatusChange.objectId
            ? {
                ...user,
                active: !nextIsDisabled
              }
            : user
        )
      );

      setSuccess(
        nextIsDisabled
          ? t("user-deactivated", { defaultValue: "User deactivated." })
          : t("user-activated", { defaultValue: "User activated." })
      );
      setIsToggleUserModalOpen(false);
      setUserPendingStatusChange(null);
    } catch (statusError) {
      console.error("Failed to update user status", statusError);
      setError(statusError?.message || t("something-went-wrong-mssg", { defaultValue: "Something went wrong." }));
    } finally {
      setIsToggleUserLoading(false);
    }
  };

  const openDeleteUserModal = (user) => {
    if (!canManageUsers || !canEditUserStatus(user)) {
      return;
    }

    setUserPendingDelete(user);
    setIsDeleteUserModalOpen(true);
  };

  const closeDeleteUserModal = () => {
    if (isDeleteUserLoading) {
      return;
    }
    setIsDeleteUserModalOpen(false);
    setUserPendingDelete(null);
  };

  const handleDeleteUser = async () => {
    if (!canManageUsers || !userPendingDelete?.objectId) {
      return;
    }

    setIsDeleteUserLoading(true);
    setError("");
    setSuccess("");

    try {
      await Parse.Cloud.run("deleteorguser", { userId: userPendingDelete.objectId });
      setTenantUsers((current) => current.filter((user) => user.objectId !== userPendingDelete.objectId));
      setSuccess(t("record-delete-alert", { defaultValue: "Record deleted successfully." }));
      setIsDeleteUserModalOpen(false);
      setUserPendingDelete(null);
    } catch (deleteError) {
      console.error("Failed to delete user", deleteError);
      setError(deleteError?.message || t("something-went-wrong-mssg", { defaultValue: "Something went wrong." }));
    } finally {
      setIsDeleteUserLoading(false);
    }
  };

  const copyGeneratedPassword = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(addUserForm.password);
      }
      setSuccess(t("copied", { defaultValue: "Copied" }));
    } catch (error) {
      console.error("Failed to copy password", error);
      setError(t("something-went-wrong-mssg", { defaultValue: "Something went wrong." }));
    }
  };

  const userExists = async (email) => {
    try {
      const response = await Parse.Cloud.run("getUserDetails", { email });
      return Boolean(response);
    } catch (error) {
      return false;
    }
  };

  const handleAddUserSubmit = async (event) => {
    event.preventDefault();
    if (!canManageUsers) {
      return;
    }

    setIsAddUserLoading(true);
    setError("");
    setSuccess("");

    try {
      const normalizedEmail = addUserForm.email.trim().toLowerCase();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!addUserForm.name.trim() || !normalizedEmail || !addUserForm.role || !addUserForm.password) {
        throw new Error(t("input-required", { defaultValue: "Please fill this field." }));
      }
      if (!emailRegex.test(normalizedEmail)) {
        throw new Error(t("invalid-email", { defaultValue: "Please enter a valid email." }));
      }

      const alreadyExists = await userExists(normalizedEmail);
      if (alreadyExists) {
        throw new Error(t("user-already-exist", { defaultValue: "User already exist." }));
      }

      const extUser = getExtUser();
      const tenantId = localStorage.getItem("TenantId") || extUser?.TenantId?.objectId;
      let organizationId = extUser?.OrganizationId?.objectId;
      
      // Fallback: if OrganizationId is missing, try to get it from TenantId
      if (!organizationId && extUser?.TenantId?.objectId) {
        try {
          const tenantResponse = await Parse.Cloud.run("gettenant", { userId: Parse.User.current()?.id });
          const tenant = typeof tenantResponse?.toJSON === "function" ? tenantResponse.toJSON() : tenantResponse;
          organizationId = tenant?.OrganizationId?.objectId || tenant?.objectId;
        } catch (tenantError) {
          console.warn("Could not fetch tenant for organization ID", tenantError);
        }
      }

      if (!tenantId || !organizationId) {
        throw new Error(
          t("new-ui.settings.users.organization-missing", {
            defaultValue: "Organization context missing. Please sign in again."
          })
        );
      }

      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Mexico_City";
      const payload = {
        name: addUserForm.name.trim(),
        email: normalizedEmail,
        phone: addUserForm.phone.trim(),
        password: addUserForm.password,
        role: addUserForm.role,
        sendOnboardingEmail: addUserForm.sendOnboardingEmail === true,
        timezone,
        tenantId,
        organization: {
          objectId: organizationId,
          company: extUser?.Company || ""
        }
      };

      const createResponse = await Parse.Cloud.run("adduser", payload);
      const createdUser = typeof createResponse?.toJSON === "function" ? createResponse.toJSON() : createResponse;
      const createdRole = createdUser?.UserRole || `contracts_${addUserForm.role}`;
      const createdObjectId = createdUser?.objectId || `${normalizedEmail}-${Date.now()}`;

      setTenantUsers((current) => [
        {
          objectId: createdObjectId,
          name: createdUser?.Name || payload.name,
          email: createdUser?.Email || payload.email,
          rawRole: createdRole,
          role: formatUserRole(createdRole, t),
          active: createdUser?.IsDisabled !== true
        },
        ...current
      ]);
      setUserRoleDraftById((current) => ({
        ...current,
        [createdObjectId]: getRoleShortValue(createdRole)
      }));

      const onboardingEmailFailed =
        payload.sendOnboardingEmail && createdUser?.onboardingEmailSent === false;

      if (onboardingEmailFailed) {
        setSuccess(
          t("new-ui.settings.users.created-without-onboarding-email", {
            defaultValue:
              "User created successfully, but the onboarding email could not be sent."
          })
        );
      } else if (payload.sendOnboardingEmail) {
        setSuccess(
          t("new-ui.settings.users.created-with-onboarding-email", {
            defaultValue: "User created successfully. Onboarding email sent."
          })
        );
      } else {
        setSuccess(t("user-created-successfully", { defaultValue: "User created successfully." }));
      }
      closeAddUserModal();
    } catch (createError) {
      console.error("Failed to create user", createError);
      setError(createError?.message || t("something-went-wrong-mssg", { defaultValue: "Something went wrong." }));
    } finally {
      setIsAddUserLoading(false);
    }
  };

  const handleSaveProfile = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const currentUser = Parse.User.current();
      if (!currentUser) {
        throw new Error("Missing active user");
      }

      currentUser.set("name", profile.name || "");
      currentUser.set("phone", profile.phone || "");
      await currentUser.save();

      const extUser = getExtUser();
      const extUserId = extUser?.objectId;
      if (extUserId) {
        const baseUrl = String(localStorage.getItem("baseUrl") || "").replace(/\/?$/, "/");
        const parseAppId = localStorage.getItem("parseAppId") || "";
        const sessionToken = localStorage.getItem("accesstoken") || "";
        const updateUrl = `${baseUrl}classes/contracts_Users/${extUserId}`;
        const response = await fetch(updateUrl, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "X-Parse-Application-Id": parseAppId,
            "X-Parse-Session-Token": sessionToken
          },
          body: JSON.stringify({
            Name: profile.name || "",
            Phone: profile.phone || "",
            JobTitle: profile.jobTitle || "",
            Language: profile.language || "es"
          })
        });

        if (!response.ok) {
          let serverMessage = "";
          try {
            const payload = await response.json();
            serverMessage = payload?.error || "";
          } catch (error) {
            serverMessage = "";
          }
          throw new Error(serverMessage || `Profile update failed (${response.status}).`);
        }
      }

      const refreshedExt = await Parse.Cloud.run("getUserDetails");
      const refreshedExtUser = typeof refreshedExt?.toJSON === "function" ? refreshedExt.toJSON() : refreshedExt;
      if (refreshedExtUser) {
        localStorage.setItem("Extand_Class", JSON.stringify([refreshedExtUser]));
      }
      localStorage.setItem("username", profile.name || "");
      localStorage.setItem("userEmail", profile.email || "");
      localStorage.setItem(
        "UserInformation",
        JSON.stringify({
          ...(parseStoredJson("UserInformation") || {}),
          name: profile.name || "",
          phone: profile.phone || "",
          email: profile.email || "",
          language: profile.language || "es",
          Language: profile.language || "es"
        })
      );

      applyPreferredLanguage(profile.language || "es");
      setSuccess(t("saved-successfully", { defaultValue: "Saved successfully." }));
    } catch (saveError) {
      console.error("Failed to save profile", saveError);
      setError(saveError?.message || t("something-went-wrong-mssg", { defaultValue: "Something went wrong." }));
    } finally {
      setIsLoading(false);
    }
  };

  const persistTenantBranding = async () => {
    const extUser = getExtUser();
    const normalizedBrandColor = normalizeHexColor(tenantPrimaryColor);

    if (!canManageTenantBranding) {
      return {
        normalizedBrandColor,
        nextLogoUrl: tenantLogoUrl || "",
        didPersist: false
      };
    }

    if (!isValidHexColor(tenantPrimaryColor)) {
      throw new Error(
        t("new-ui.settings.branding.invalid-color", {
          defaultValue: "Corporate color must be a valid HEX value."
        })
      );
    }

    const currentTenantId = tenantId || extUser?.TenantId?.objectId || localStorage.getItem("TenantId");
    if (!currentTenantId) {
      throw new Error(
        t("new-ui.settings.users.organization-missing", {
          defaultValue: "Organization context missing. Please sign in again."
        })
      );
    }

    let nextLogoUrl = tenantLogoUrl || "";
    if (tenantLogoFile) {
      nextLogoUrl = await uploadTenantLogoAsset(tenantLogoFile);
    }

    const tenantDetails = {
      PrimaryColor: normalizedBrandColor,
      ThemeColor: normalizedBrandColor,
      primaryColor: normalizedBrandColor,
      themeColor: normalizedBrandColor
    };
    if (nextLogoUrl) {
      tenantDetails.Logo = nextLogoUrl;
    }

    await Parse.Cloud.run("updatetenant", {
      tenantId: currentTenantId,
      details: tenantDetails
    });

    setTenantId(currentTenantId);
    setTenantLogoUrl(nextLogoUrl || tenantLogoUrl);
    setTenantLogoPreview(nextLogoUrl || tenantLogoUrl || DEFAULT_TENANT_LOGO_URL);
    setTenantLogoFile(null);
    if (tenantLogoInputRef.current) {
      tenantLogoInputRef.current.value = "";
    }
    setTenantPrimaryColor(normalizedBrandColor);
    localStorage.setItem("appLogo", nextLogoUrl || "");
    window.dispatchEvent(new CustomEvent("dana:tenant-brand-updated"));

    return {
      normalizedBrandColor,
      nextLogoUrl,
      didPersist: true
    };
  };

  const syncTenantEmailTemplateBranding = async (currentTenantId, brandingOverride = {}) => {
    if (!canManageTenantEmailTemplates || !currentTenantId) {
      return;
    }

    const resolvedBranding = {
      logoUrl: String(brandingOverride?.logoUrl || tenantLogoUrl || DEFAULT_TENANT_LOGO_URL).trim() || DEFAULT_TENANT_LOGO_URL,
      themeColor: normalizeHexColor(brandingOverride?.themeColor || tenantPrimaryColor)
    };

    const templateDetails = {};
    if (String(requestSubject || "").trim() && !isTemplateBodyEmpty(requestBodyMode, requestBody)) {
      templateDetails.RequestSubject = requestSubject;
      templateDetails.RequestBodyMode = requestBodyMode;
      templateDetails.RequestBody = buildStoredTemplateBody(
        "request",
        requestBodyMode,
        requestBody,
        emailShellCopy,
        resolvedBranding
      ).replace(/"/g, "'");
    }
    if (String(completionSubject || "").trim() && !isTemplateBodyEmpty(completionBodyMode, completionBody)) {
      templateDetails.CompletionSubject = completionSubject;
      templateDetails.CompletionBodyMode = completionBodyMode;
      templateDetails.CompletionBody = buildStoredTemplateBody(
        "completion",
        completionBodyMode,
        completionBody,
        emailShellCopy,
        resolvedBranding
      ).replace(/"/g, "'");
    }

    if (!Object.keys(templateDetails).length) {
      return;
    }

    await Parse.Cloud.run("updatetenant", {
      tenantId: currentTenantId,
      details: templateDetails
    });
  };

  const handleSaveTenantBranding = async () => {
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const { normalizedBrandColor, nextLogoUrl } = await persistTenantBranding();
      const extUser = getExtUser();
      const currentTenantId = tenantId || extUser?.TenantId?.objectId || localStorage.getItem("TenantId");
      await syncTenantEmailTemplateBranding(currentTenantId, {
        logoUrl: nextLogoUrl || tenantLogoUrl || DEFAULT_TENANT_LOGO_URL,
        themeColor: normalizedBrandColor
      });
      const nextExtUser = {
        ...extUser,
        TenantId: {
          ...(extUser?.TenantId || {}),
          PrimaryColor: normalizedBrandColor,
          ThemeColor: normalizedBrandColor,
          primaryColor: normalizedBrandColor,
          themeColor: normalizedBrandColor,
          ...(nextLogoUrl ? { Logo: nextLogoUrl } : {})
        }
      };
      localStorage.setItem("Extand_Class", JSON.stringify([nextExtUser]));
      setSuccess(t("saved-successfully", { defaultValue: "Saved successfully." }));
    } catch (brandingError) {
      console.error("Failed to save tenant branding", brandingError);
      setError(
        brandingError?.message || t("something-went-wrong-mssg", { defaultValue: "Something went wrong." })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveTenantSignatureValidation = async () => {
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      if (!canManageTenantSignatureValidation) {
        throw new Error(
          t("something-went-wrong-mssg", {
            defaultValue: "You do not have permission to update preferences."
          })
        );
      }

      const parsedMinArea = parseInt(tenantSignatureValidation.minSignatureArea, 10);
      const parsedMinLength = parseInt(tenantSignatureValidation.minSignatureLength, 10);
      if (
        Number.isNaN(parsedMinArea) ||
        Number.isNaN(parsedMinLength) ||
        parsedMinArea < DEFAULT_SIGNATURE_VALIDATION.minAreaFloor ||
        parsedMinLength < DEFAULT_SIGNATURE_VALIDATION.minLengthFloor ||
        parsedMinArea > DEFAULT_SIGNATURE_VALIDATION.maxArea ||
        parsedMinLength > DEFAULT_SIGNATURE_VALIDATION.maxLength
      ) {
        throw new Error(
          t("signature-threshold-invalid", {
            defaultValue: "Signature validation thresholds are invalid."
          })
        );
      }

      const extUser = getExtUser();
      const currentTenantId = tenantId || extUser?.TenantId?.objectId || localStorage.getItem("TenantId");
      if (!currentTenantId) {
        throw new Error(
          t("new-ui.settings.users.organization-missing", {
            defaultValue: "Organization context missing. Please sign in again."
          })
        );
      }

      await Parse.Cloud.run("updatetenant", {
        tenantId: currentTenantId,
        details: {
          MinSignatureArea: parsedMinArea,
          MinSignatureLength: parsedMinLength
        }
      });

      const nextExtUser = {
        ...extUser,
        TenantId: {
          ...(extUser?.TenantId || {}),
          MinSignatureArea: parsedMinArea,
          MinSignatureLength: parsedMinLength
        }
      };
      localStorage.setItem("Extand_Class", JSON.stringify([nextExtUser]));
      setTenantSignatureValidation({
        minSignatureArea: String(parsedMinArea),
        minSignatureLength: String(parsedMinLength)
      });
      setSuccess(t("saved-successfully", { defaultValue: "Saved successfully." }));
    } catch (validationError) {
      console.error("Failed to save tenant signature validation", validationError);
      setError(
        validationError?.message || t("something-went-wrong-mssg", { defaultValue: "Something went wrong." })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveTenantPersonalEmailPermission = async () => {
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      if (!canManagePersonalEmailPermission) {
        throw new Error(
          t("something-went-wrong-mssg", {
            defaultValue: "You do not have permission to update this setting."
          })
        );
      }

      const currentTenantId = tenantId || getExtUser()?.TenantId?.objectId || localStorage.getItem("TenantId");
      if (!currentTenantId) {
        throw new Error(
          t("new-ui.settings.users.organization-missing", {
            defaultValue: "Organization context missing. Please sign in again."
          })
        );
      }

      const nextAllowAdmin = Boolean(allowPersonalEmailTemplatesForAdmin);
      const nextAllowEditor = Boolean(allowPersonalEmailTemplatesForEditor);
      const nextAllowUser = Boolean(allowPersonalEmailTemplatesForUser);
      const nextLegacyCombined = nextAllowAdmin || nextAllowEditor;
      await Parse.Cloud.run("updatetenant", {
        tenantId: currentTenantId,
        details: {
          AllowPersonalEmailTemplatesForAdmin: nextAllowAdmin,
          AllowPersonalEmailTemplatesForEditor: nextAllowEditor,
          AllowPersonalEmailTemplatesForUser: nextAllowUser,
          AllowPersonalEmailTemplatesForEditorOrgAdmin: nextLegacyCombined
        }
      });

      const extUser = getExtUser();
      const nextExtUser = {
        ...extUser,
        TenantId: {
          ...(extUser?.TenantId || {}),
          AllowPersonalEmailTemplatesForAdmin: nextAllowAdmin,
          AllowPersonalEmailTemplatesForEditor: nextAllowEditor,
          AllowPersonalEmailTemplatesForUser: nextAllowUser,
          AllowPersonalEmailTemplatesForEditorOrgAdmin: nextLegacyCombined
        }
      };
      localStorage.setItem("Extand_Class", JSON.stringify([nextExtUser]));
      setSuccess(t("saved-successfully", { defaultValue: "Saved successfully." }));
    } catch (permissionError) {
      console.error("Failed to save tenant personal email permission", permissionError);
      setError(
        permissionError?.message || t("something-went-wrong-mssg", { defaultValue: "Something went wrong." })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePersonalRequestEmailTemplate = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      if (!canUsePersonalEmailTemplates) {
        throw new Error(
          t("something-went-wrong-mssg", {
            defaultValue: "You do not have permission to update personal email templates."
          })
        );
      }

      const params = {
        UsePersonalRequestEmailByDefault: Boolean(usePersonalRequestEmailByDefault)
      };
      
      // Always validate and save personal template fields
      if (!String(personalRequestSubject || "").trim()) {
        throw new Error(t("input-required", { defaultValue: "Please fill this field." }));
      }
      if (isTemplateBodyEmpty(personalRequestBodyMode, personalRequestBody)) {
        throw new Error(t("input-required", { defaultValue: "Please fill this field." }));
      }

      const serializedTemplateBody = buildStoredTemplateBody(
        "request",
        personalRequestBodyMode,
        personalRequestBody,
        emailShellCopy,
        emailBranding
      ).replace(/"/g, "'");

      params.PersonalRequestSubject = personalRequestSubject.trim();
      params.PersonalRequestBody = serializedTemplateBody;
      params.PersonalRequestBodyMode = personalRequestBodyMode;

      await Parse.Cloud.run("updatepreferences", params);

      const extUser = getExtUser();
      const nextExtUser = {
        ...extUser,
        UsePersonalRequestEmailByDefault: params.UsePersonalRequestEmailByDefault,
        PersonalRequestSubject: params.PersonalRequestSubject,
        PersonalRequestBody: params.PersonalRequestBody,
        PersonalRequestBodyMode: params.PersonalRequestBodyMode
      };
      localStorage.setItem("Extand_Class", JSON.stringify([nextExtUser]));

      setSuccess(t("saved-successfully", { defaultValue: "Saved successfully." }));
    } catch (personalTemplateError) {
      console.error("Failed to save personal request email template", personalTemplateError);
      setError(
        personalTemplateError?.message || t("something-went-wrong-mssg", { defaultValue: "Something went wrong." })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePreferences = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      if (!canUsePreferences) {
        throw new Error(
          t("something-went-wrong-mssg", {
            defaultValue: "You do not have permission to update preferences."
          })
        );
      }

      const enabledSignatureTypes = preferences.signatureTypes.filter((item) => item.enabled);
      if (!enabledSignatureTypes.length) {
        throw new Error(
          t("at-least-one-signature-type", {
            defaultValue: "At least one signature type should be enabled."
          })
        );
      }

      const onlyDefaultEnabled =
        enabledSignatureTypes.length === 1 && enabledSignatureTypes[0].name === "default";
      if (onlyDefaultEnabled) {
        throw new Error(
          t("expect-default-one-signature-type", {
            defaultValue: "Please enable one additional signature type apart from the default."
          })
        );
      }
      const defaultTimeToCompleteDays = Number.parseInt(preferences.defaultTimeToCompleteDays, 10);
      if (!Number.isFinite(defaultTimeToCompleteDays) || defaultTimeToCompleteDays <= 0) {
        throw new Error(
          t("new-ui.settings.default-time-to-complete.invalid", {
            defaultValue: "Default completion days must be a positive number."
          })
        );
      }

      const params = {
        NotifyOnSignatures: preferences.notifyOnSignatures,
        SendinOrder: preferences.sendInOrder,
        IsEnableOTP: preferences.enableOtp,
        IsTourEnabled: preferences.isTourEnabled,
        DateFormat: preferences.dateFormat,
        Is12HourTime: preferences.is12HourTime,
        IsLTVEnabled: true,
        AutomaticReminders: preferences.automaticReminders,
        RemindOnceInEvery: parseInt(preferences.remindOnceInEvery, 10) || 5,
        DefaultTimeToCompleteDays: defaultTimeToCompleteDays,
        SignatureType: preferences.signatureTypes,
        Timezone: preferences.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      await Parse.Cloud.run("updatepreferences", params);

      const extUser = getExtUser();
      const nextExtUser = {
        ...extUser,
        NotifyOnSignatures: params.NotifyOnSignatures,
        SendinOrder: params.SendinOrder,
        IsEnableOTP: params.IsEnableOTP,
        IsTourEnabled: params.IsTourEnabled,
        DateFormat: params.DateFormat,
        Is12HourTime: params.Is12HourTime,
        IsLTVEnabled: true,
        AutomaticReminders: params.AutomaticReminders,
        RemindOnceInEvery: params.RemindOnceInEvery,
        DefaultTimeToCompleteDays: params.DefaultTimeToCompleteDays,
        SignatureType: params.SignatureType,
        Timezone: params.Timezone
      };
      localStorage.setItem("Extand_Class", JSON.stringify([nextExtUser]));
      setSuccess(t("saved-successfully", { defaultValue: "Saved successfully." }));
    } catch (prefError) {
      console.error("Failed to save preferences", prefError);
      setError(prefError?.message || t("something-went-wrong-mssg", { defaultValue: "Something went wrong." }));
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSignatureType = (name) => {
    setPreferences((current) => ({
      ...current,
      signatureTypes: current.signatureTypes.map((item) =>
        item.name === name ? { ...item, enabled: !item.enabled } : item
      )
    }));
  };

  const emailVariableButtons = useMemo(
    () =>
      EMAIL_TEMPLATE_VARIABLES.map((item) => ({
        ...item,
        label: t(item.labelKey, { defaultValue: item.labelFallback })
      })),
    [t]
  );

  const insertVariableIntoPlainBody = (kind, token) => {
    const isRequest = kind === "request";
    const isCompletion = kind === "completion";
    const currentBody = isRequest ? requestBody : isCompletion ? completionBody : personalRequestBody;
    const setter = isRequest ? setRequestBody : isCompletion ? setCompletionBody : setPersonalRequestBody;
    const inputRef = isRequest
      ? requestPlainBodyRef
      : isCompletion
        ? completionPlainBodyRef
        : personalRequestPlainBodyRef;
    const input = inputRef.current;
    const nextToken = String(token || "");
    if (!nextToken) {
      return;
    }

    if (!input) {
      setter((current) => `${current || ""}${nextToken}`);
      return;
    }

    const start = Number.isInteger(input.selectionStart) ? input.selectionStart : currentBody.length;
    const end = Number.isInteger(input.selectionEnd) ? input.selectionEnd : currentBody.length;
    const nextValue = `${currentBody.slice(0, start)}${nextToken}${currentBody.slice(end)}`;
    setter(nextValue);
    requestAnimationFrame(() => {
      input.focus();
      const cursor = start + nextToken.length;
      input.setSelectionRange(cursor, cursor);
    });
  };

  const insertVariableIntoRichBody = (kind, token) => {
    const isRequest = kind === "request";
    const isCompletion = kind === "completion";
    const editorRef = isRequest
      ? requestEmailEditorRef
      : isCompletion
        ? completionEmailEditorRef
        : personalRequestEmailEditorRef;
    const setter = isRequest ? setRequestBody : isCompletion ? setCompletionBody : setPersonalRequestBody;
    const nextToken = String(token || "");
    if (!nextToken) {
      return;
    }

    const editor = editorRef.current?.getEditor?.();
    if (!editor) {
      setter((current) => `${current || ""}${nextToken}`);
      return;
    }

    const selection = editor.getSelection(true);
    const index = selection?.index ?? editor.getLength();
    editor.insertText(index, nextToken, "user");
    editor.setSelection(index + nextToken.length, 0, "user");
    setter(editor.root.innerHTML);
  };

  const insertTemplateVariable = (kind, token) => {
    const templateMode =
      kind === "request"
        ? requestBodyMode
        : kind === "completion"
          ? completionBodyMode
          : personalRequestBodyMode;
    if (templateMode === EMAIL_TEMPLATE_MODE.PLAIN) {
      insertVariableIntoPlainBody(kind, token);
      return;
    }
    insertVariableIntoRichBody(kind, token);
  };

  const handleVariableDragStart = (token) => (event) => {
    event.dataTransfer.setData("text/plain", token);
    event.dataTransfer.effectAllowed = "copy";
  };

  const handleVariableDrop = (kind) => (event) => {
    event.preventDefault();
    const token = event.dataTransfer.getData("text/plain");
    if (!token) {
      return;
    }
    insertTemplateVariable(kind, token);
  };

  const handleTemplateModeChange = (kind, nextMode) => {
    const normalizedMode = normalizeEmailTemplateMode(nextMode);
    if (kind === "request") {
      if (requestBodyMode === normalizedMode) {
        return;
      }
      if (requestBodyMode === EMAIL_TEMPLATE_MODE.HTML && normalizedMode === EMAIL_TEMPLATE_MODE.PLAIN) {
        setRequestBodyHtmlDraft(requestBody || "");
      }
      if (requestBodyMode === EMAIL_TEMPLATE_MODE.PLAIN && normalizedMode === EMAIL_TEMPLATE_MODE.HTML) {
        if (String(requestBodyHtmlDraft || "").length > 0) {
          setRequestBody(requestBodyHtmlDraft);
          setRequestBodyMode(normalizedMode);
          return;
        }
      }
      setRequestBody(convertTemplateBodyBetweenModes(requestBody, requestBodyMode, normalizedMode));
      setRequestBodyMode(normalizedMode);
      return;
    }
    if (kind === "personal-request") {
      if (personalRequestBodyMode === normalizedMode) {
        return;
      }
      if (personalRequestBodyMode === EMAIL_TEMPLATE_MODE.HTML && normalizedMode === EMAIL_TEMPLATE_MODE.PLAIN) {
        setPersonalRequestBodyHtmlDraft(personalRequestBody || "");
      }
      if (personalRequestBodyMode === EMAIL_TEMPLATE_MODE.PLAIN && normalizedMode === EMAIL_TEMPLATE_MODE.HTML) {
        if (String(personalRequestBodyHtmlDraft || "").length > 0) {
          setPersonalRequestBody(personalRequestBodyHtmlDraft);
          setPersonalRequestBodyMode(normalizedMode);
          return;
        }
      }
      setPersonalRequestBody(
        convertTemplateBodyBetweenModes(personalRequestBody, personalRequestBodyMode, normalizedMode)
      );
      setPersonalRequestBodyMode(normalizedMode);
      return;
    }

    if (completionBodyMode === normalizedMode) {
      return;
    }
    if (completionBodyMode === EMAIL_TEMPLATE_MODE.HTML && normalizedMode === EMAIL_TEMPLATE_MODE.PLAIN) {
      setCompletionBodyHtmlDraft(completionBody || "");
    }
    if (completionBodyMode === EMAIL_TEMPLATE_MODE.PLAIN && normalizedMode === EMAIL_TEMPLATE_MODE.HTML) {
      if (String(completionBodyHtmlDraft || "").length > 0) {
        setCompletionBody(completionBodyHtmlDraft);
        setCompletionBodyMode(normalizedMode);
        return;
      }
    }
    setCompletionBody(convertTemplateBodyBetweenModes(completionBody, completionBodyMode, normalizedMode));
    setCompletionBodyMode(normalizedMode);
  };

  const buildEmailTemplatePreview = (kind) => {
    const isPersonalRequest = kind === "personal-request";
    const isRequest = kind === "request" || isPersonalRequest;
    const templateSubject = isPersonalRequest
      ? personalRequestSubject
      : isRequest
        ? requestSubject
        : completionSubject;
    const templateBody = isPersonalRequest ? personalRequestBody : isRequest ? requestBody : completionBody;
    const templateMode = isPersonalRequest
      ? personalRequestBodyMode
      : isRequest
        ? requestBodyMode
        : completionBodyMode;
    const isPlainTemplate = templateMode === EMAIL_TEMPLATE_MODE.PLAIN;

    const sampleVariables = {
      document_title: t("new-ui.settings.email.preview-sample.document-title", {
        defaultValue: "Service agreement 2026"
      }),
      note: t("new-ui.settings.email.preview-sample.note", {
        defaultValue: "Please review and sign to complete the process."
      }),
      sender_name: t("new-ui.settings.email.preview-sample.sender-name", {
        defaultValue: "Alex Smith"
      }),
      sender_mail: "alex.smith@company.com",
      sender_phone: "+1 555 0199",
      receiver_name: t("new-ui.settings.email.preview-sample.receiver-name", {
        defaultValue: "Taylor Johnson"
      }),
      receiver_email: "taylor.johnson@client.com",
      receiver_phone: "+1 555 0144",
      expiry_date: "2026-03-31",
      company_name: tentan || "DANASign",
      tenant_logo_url: emailBranding.logoUrl,
      tenant_theme_color: emailBranding.themeColor,
      signing_url: isRequest
        ? isPlainTemplate
        ? "https://example.com/sign/abc123"
        : `<a href='https://example.com/sign/abc123' target='_blank' style='display:inline-block;padding:12px 28px;background-color:${emailBranding.themeColor};color:#ffffff;font-weight:700;text-decoration:none;border-radius:4px'>${t(
            "sign-now",
            {
              defaultValue: "Sign now"
            }
          )}</a>`
        : ""
    };

    const templateKind = isRequest ? "request" : "completion";
    const storedBody = buildStoredTemplateBody(
      templateKind,
      templateMode,
      templateBody,
      emailShellCopy,
      emailBranding
    );
    return {
      subject: replaceTemplateVariables(templateSubject, sampleVariables),
      html: replaceTemplateVariables(storedBody, sampleVariables),
      isPlain: isPlainTemplate
    };
  };

  const openEmailTemplatePreview = (kind) => {
    const isPersonalRequest = kind === "personal-request";
    const isRequest = kind === "request" || isPersonalRequest;
    const templateSubject = isPersonalRequest
      ? personalRequestSubject
      : isRequest
        ? requestSubject
        : completionSubject;
    const templateBody = isPersonalRequest ? personalRequestBody : isRequest ? requestBody : completionBody;
    const templateMode = isPersonalRequest
      ? personalRequestBodyMode
      : isRequest
        ? requestBodyMode
        : completionBodyMode;

    if (!String(templateSubject || "").trim() || isTemplateBodyEmpty(templateMode, templateBody)) {
      setError(
        t("new-ui.settings.email.preview-requires-content", {
          defaultValue: "Complete subject and body before preview."
        })
      );
      return;
    }

    const preview = buildEmailTemplatePreview(kind);
    setEmailPreviewSubject(preview.subject);
    setEmailPreviewHtml(preview.isPlain ? preview.html : disablePreviewLinks(preview.html));
    setEmailPreviewIsPlain(preview.isPlain);
    setIsEmailPreviewOpen(true);
  };

  const saveTenantEmailTemplate = async (kind) => {
    if (!canManageTenantEmailTemplates) {
      return;
    }

    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const currentTenantId = tenantId || getExtUser()?.TenantId?.objectId || localStorage.getItem("TenantId");
      if (!currentTenantId) {
        throw new Error(
          t("new-ui.settings.users.organization-missing", {
            defaultValue: "Organization context missing. Please sign in again."
          })
        );
      }

      const isRequestTemplate = kind === "request";
      const templateMode = isRequestTemplate ? requestBodyMode : completionBodyMode;
      const templateSubject = isRequestTemplate ? requestSubject : completionSubject;
      const templateBody = isRequestTemplate ? requestBody : completionBody;

      if (!String(templateSubject || "").trim()) {
        throw new Error(t("input-required", { defaultValue: "Please fill this field." }));
      }
      if (isTemplateBodyEmpty(templateMode, templateBody)) {
        throw new Error(t("input-required", { defaultValue: "Please fill this field." }));
      }

      const serializedTemplateBody = buildStoredTemplateBody(
        kind,
        templateMode,
        templateBody,
        emailShellCopy,
        emailBranding
      ).replace(/"/g, "'");

      const details =
        isRequestTemplate
          ? {
              RequestSubject: templateSubject,
              RequestBody: serializedTemplateBody,
              RequestBodyMode: templateMode
            }
          : {
              CompletionSubject: templateSubject,
              CompletionBody: serializedTemplateBody,
              CompletionBodyMode: templateMode
            };

      await Parse.Cloud.run("updatetenant", {
        tenantId: currentTenantId,
        details
      });

      setSuccess(t("saved-successfully", { defaultValue: "Saved successfully." }));
    } catch (templateError) {
      console.error("Failed to save email template", templateError);
      setError(templateError?.message || t("something-went-wrong-mssg", { defaultValue: "Something went wrong." }));
    } finally {
      setIsLoading(false);
    }
  };

  const openTestEmailModal = (emailType) => {
    const currentUser = Parse.User.current();
    const userEmail = currentUser?.getEmail?.() || localStorage.getItem("userEmail") || "";
    setTestEmailRecipient(userEmail);
    setTestEmailType(emailType);
    setIsTestEmailModalOpen(true);
    setError("");
    setSuccess("");
  };

  const sendTestEmail = async () => {
    if (!testEmailRecipient || !testEmailRecipient.trim()) {
      setError(t("input-required", { defaultValue: "Please fill this field." }));
      return;
    }

    setIsSendingTestEmail(true);
    setError("");
    setSuccess("");

    try {
      const result = await Parse.Cloud.run("sendtestemail", {
        recipientEmail: testEmailRecipient.trim(),
        emailType: testEmailType
      });

      if (result?.success) {
        setSuccess(
          t("new-ui.settings.email.test-email-sent", {
            email: testEmailRecipient,
            defaultValue: "Test email sent to {{email}}"
          })
        );
        setIsTestEmailModalOpen(false);
      } else {
        throw new Error("Failed to send test email");
      }
    } catch (testEmailError) {
      console.error("Failed to send test email", testEmailError);
      setError(
        testEmailError?.message ||
          t("new-ui.settings.email.test-email-failed", {
            defaultValue: "Failed to send test email. Please try again."
          })
      );
    } finally {
      setIsSendingTestEmail(false);
    }
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
        throw new Error(t("input-required", { defaultValue: "Please fill this field." }));
      }
      if (passwordForm.newPassword !== passwordForm.confirmPassword) {
        throw new Error(t("password-update-alert-4", { defaultValue: "Passwords do not match." }));
      }
      if (passwordForm.newPassword.length < 8) {
        throw new Error(t("password-length", { defaultValue: "Password should be 8 characters long" }));
      }

      const email = localStorage.getItem("userEmail") || Parse.User.current()?.getEmail?.();
      if (!email) {
        throw new Error(t("something-went-wrong-mssg", { defaultValue: "Something went wrong." }));
      }

      const reauthUser = await Parse.User.logIn(email, passwordForm.currentPassword);
      reauthUser.set("password", passwordForm.newPassword);
      const savedUser = await reauthUser.save();

      if (savedUser?.getSessionToken?.()) {
        const nextToken = savedUser.getSessionToken();
        await Parse.User.become(nextToken);
        localStorage.setItem("accesstoken", nextToken);
      }

      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
      setSuccess(t("password-update-alert-1", { defaultValue: "Password updated successfully." }));
    } catch (passwordError) {
      console.error("Failed to change password", passwordError);
      setError(passwordError?.message || t("something-went-wrong-mssg", { defaultValue: "Something went wrong." }));
    } finally {
      setIsLoading(false);
    }
  };

  const uploadSignatureAsset = async (file) => {
    const parseFile = new Parse.File(file.name, file);
    const savedFile = await parseFile.save();
    const rawUrl = savedFile?.url?.() || "";
    if (!rawUrl) {
      throw new Error(t("something-went-wrong-mssg", { defaultValue: "Something went wrong." }));
    }

    // Store the raw URL without JWT token for persistent access
    // The URL will be signed on-demand when needed for temporary access
    return rawUrl;
  };

  const getSignedSignatureUrl = async (rawUrl) => {
    if (!rawUrl) return "";
    
    // If it's already a remote URL (S3/DigitalOcean), return as-is
    if (!rawUrl.includes("/files/")) {
      return rawUrl;
    }

    // For local files, sign the URL with JWT token
    try {
      const secured = await Parse.Cloud.run("fileupload", { url: rawUrl });
      if (secured?.url) {
        return secured.url;
      }
    } catch (signError) {
      console.error("Failed to sign signature URL", signError);
    }
    
    return rawUrl;
  };

  const uploadTenantLogoAsset = async (file) => {
    if (!file || !String(file.type || "").startsWith("image/")) {
      throw new Error(
        t("new-ui.settings.branding.invalid-image", {
          defaultValue: "Please select a valid image file."
        })
      );
    }

    const uploadViaCustomRoute = async () => {
      const serverBaseUrl = resolveServerBaseUrl();
      const uploadUrl = `${serverBaseUrl}/file_upload`;
      const parseAppId = String(localStorage.getItem("parseAppId") || "").trim();
      const sessionToken = String(localStorage.getItem("accesstoken") || "").trim();
      const formData = new FormData();
      formData.append("file", file, file.name);

      const response = await fetch(uploadUrl, {
        method: "POST",
        headers: {
          ...(parseAppId ? { "X-Parse-Application-Id": parseAppId } : {}),
          ...(sessionToken ? { "X-Parse-Session-Token": sessionToken } : {})
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Logo upload failed (${response.status})`);
      }

      const payload = await response.json().catch(() => ({}));
      const imageUrl = String(payload?.imageUrl || payload?.url || "").trim();
      if (!imageUrl) {
        throw new Error("Logo upload did not return a URL.");
      }
      return imageUrl;
    };

    try {
      return await uploadViaCustomRoute();
    } catch (customUploadError) {
      console.error("Custom logo upload failed, falling back to Parse file", customUploadError);
    }

    const parseFile = new Parse.File(file.name, file);
    const savedFile = await parseFile.save();
    const rawUrl = savedFile?.url?.() || "";
    if (!rawUrl) {
      throw new Error(t("something-went-wrong-mssg", { defaultValue: "Something went wrong." }));
    }
    return rawUrl;
  };

  const handleTenantLogoSelection = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setTenantLogoFile(null);
      setTenantLogoPreview(tenantLogoUrl || DEFAULT_TENANT_LOGO_URL);
      return;
    }

    const maxBytes = TENANT_LOGO_MAX_SIZE_MB * 1024 * 1024;
    if (!String(file.type || "").startsWith("image/")) {
      setError(
        t("new-ui.settings.branding.invalid-image", {
          defaultValue: "Please select a valid image file."
        })
      );
      event.target.value = "";
      return;
    }
    if (file.size > maxBytes) {
      setError(
        t("new-ui.settings.branding.logo-size-limit", {
          max: TENANT_LOGO_MAX_SIZE_MB,
          defaultValue: "Logo must be {{max}}MB or less."
        })
      );
      event.target.value = "";
      return;
    }

    try {
      const dataUrl = await fileToDataUrl(file);
      setTenantLogoFile(file);
      setTenantLogoPreview(dataUrl || tenantLogoUrl || DEFAULT_TENANT_LOGO_URL);
      setError("");
    } catch (uploadError) {
      console.error("Failed to read tenant logo", uploadError);
      setError(t("something-went-wrong-mssg", { defaultValue: "Something went wrong." }));
      event.target.value = "";
    }
  };

  const handleSignatureDrawEnd = () => {
    signatureUploadRef.current && (signatureUploadRef.current.value = "");
    setSignatureImage("");
    setSignatureNaturalWH({});
    if (signatureCanvasRef.current?.isEmpty?.()) {
      setSignatureDataUrl("");
      return;
    }
    setSignatureDataUrl(signatureCanvasRef.current.toDataURL("image/png"));
  };

  const handleInitialsDrawEnd = () => {
    initialsUploadRef.current && (initialsUploadRef.current.value = "");
    setInitialsImage("");
    setInitialsNaturalWH({});
    if (initialsCanvasRef.current?.isEmpty?.()) {
      setInitialsDataUrl("");
      return;
    }
    setInitialsDataUrl(initialsCanvasRef.current.toDataURL("image/png"));
  };

  const handleSignatureUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const dataUrl = await fileToDataUrl(file);
      const naturalWH = await getImageSize(dataUrl);
      signatureCanvasRef.current?.clear?.();
      setSignatureDataUrl("");
      setSignatureImage(dataUrl);
      setSignatureNaturalWH(naturalWH);
      setError("");
    } catch (uploadError) {
      console.error("Failed to process signature image", uploadError);
      setError(t("something-went-wrong-mssg", { defaultValue: "Something went wrong." }));
    }
  };

  const handleInitialsUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const dataUrl = await fileToDataUrl(file);
      const naturalWH = await getImageSize(dataUrl);
      initialsCanvasRef.current?.clear?.();
      setInitialsDataUrl("");
      setInitialsImage(dataUrl);
      setInitialsNaturalWH(naturalWH);
      setError("");
    } catch (uploadError) {
      console.error("Failed to process initials image", uploadError);
      setError(t("something-went-wrong-mssg", { defaultValue: "Something went wrong." }));
    }
  };

  const clearSignatureInput = () => {
    signatureCanvasRef.current?.clear?.();
    if (signatureUploadRef.current) {
      signatureUploadRef.current.value = "";
    }
    setSignatureImage("");
    setSignedSignatureUrl("");
    setSignatureDataUrl("");
    setSignatureNaturalWH({});
  };

  const clearInitialsInput = () => {
    initialsCanvasRef.current?.clear?.();
    if (initialsUploadRef.current) {
      initialsUploadRef.current.value = "";
    }
    setInitialsImage("");
    setSignedInitialsUrl("");
    setInitialsDataUrl("");
    setInitialsNaturalWH({});
    setInitialsText("");
  };

  const generateInitialsFromText = (text) => {
    setInitialsText(text);
    if (!text.trim()) {
      setInitialsDataUrl("");
      setInitialsImage("");
      setInitialsNaturalWH({});
      return;
    }
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const fontSize = 64;
    const fontFamily = "'Brush Script MT', 'Segoe Script', 'Dancing Script', cursive";
    ctx.font = `${fontSize}px ${fontFamily}`;
    const metrics = ctx.measureText(text.trim());
    const textWidth = Math.ceil(metrics.width) + 40;
    const textHeight = fontSize + 40;
    canvas.width = textWidth;
    canvas.height = textHeight;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.font = `${fontSize}px ${fontFamily}`;
    ctx.fillStyle = initialsPenColor;
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.fillText(text.trim(), textWidth / 2, textHeight / 2);
    const dataUrl = canvas.toDataURL("image/png");
    initialsCanvasRef.current?.clear?.();
    setInitialsImage("");
    setInitialsDataUrl(dataUrl);
    setInitialsNaturalWH({ width: canvas.width, height: canvas.height });
  };

  const handleSaveSignature = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const currentUser = Parse.User.current();
      if (!currentUser?.id) {
        throw new Error(t("something-went-wrong-mssg", { defaultValue: "Something went wrong." }));
      }

      const normalizedName = normalizeSignatureName(
        signatureName || currentUser.get("name") || profile.name || localStorage.getItem("username")
      );
      const minAreaThreshold = parseThreshold(
        preferences.minSignatureArea,
        DEFAULT_SIGNATURE_VALIDATION.minArea
      );
      const minLengthThreshold = parseThreshold(
        preferences.minSignatureLength,
        DEFAULT_SIGNATURE_VALIDATION.minLength
      );

      let finalSignatureUrl = "";
      const isRemoteSignature = /^https?:\/\//i.test(signatureImage);
      if (signatureDataUrl) {
        const valid = validateDrawnSignature(signatureCanvasRef, minAreaThreshold, minLengthThreshold);
        if (!valid) {
          throw new Error(t("signature-too-small", { defaultValue: "The signature is too small. Please draw a larger signature." }));
        }
        const signatureFile = dataUrlToFile(signatureDataUrl, `${normalizedName}_signature`);
        if (!signatureFile) {
          throw new Error(t("something-went-wrong-mssg", { defaultValue: "Something went wrong." }));
        }
        finalSignatureUrl = await uploadSignatureAsset(signatureFile);
      } else if (signatureImage) {
        if (isRemoteSignature) {
          finalSignatureUrl = signatureImage;
        } else {
          const valid = validateImageSignature(signatureNaturalWH, minAreaThreshold, minLengthThreshold);
          if (!valid) {
            throw new Error(t("signature-too-small", { defaultValue: "The signature is too small. Please draw a larger signature." }));
          }
          const signatureFile = dataUrlToFile(signatureImage, `${normalizedName}_signature`);
          if (!signatureFile) {
            throw new Error(t("something-went-wrong-mssg", { defaultValue: "Something went wrong." }));
          }
          finalSignatureUrl = await uploadSignatureAsset(signatureFile);
        }
      }

      if (!finalSignatureUrl) {
        throw new Error(t("upload-signature/Image", { defaultValue: "Please upload signature/Image" }));
      }

      let finalInitialsUrl = "";
      const isRemoteInitials = /^https?:\/\//i.test(initialsImage);
      if (initialsDataUrl) {
        const initialsFile = dataUrlToFile(initialsDataUrl, `${normalizedName}_initials`);
        if (!initialsFile) {
          throw new Error(t("something-went-wrong-mssg", { defaultValue: "Something went wrong." }));
        }
        finalInitialsUrl = await uploadSignatureAsset(initialsFile);
      } else if (initialsImage) {
        if (isRemoteInitials) {
          finalInitialsUrl = initialsImage;
        } else {
          const initialsFile = dataUrlToFile(initialsImage, `${normalizedName}_initials`);
          if (!initialsFile) {
            throw new Error(t("something-went-wrong-mssg", { defaultValue: "Something went wrong." }));
          }
          finalInitialsUrl = await uploadSignatureAsset(initialsFile);
        }
      }

      const signatureObject = new Parse.Object("contracts_Signature");
      if (signatureRecordId) {
        signatureObject.id = signatureRecordId;
      }
      signatureObject.set("SignatureName", normalizedName);
      signatureObject.set("ImageURL", finalSignatureUrl);
      signatureObject.set("Initials", finalInitialsUrl || "");
      signatureObject.set("UserId", {
        __type: "Pointer",
        className: "_User",
        objectId: currentUser.id
      });

      const savedSignature = await signatureObject.save();
      setSignatureRecordId(savedSignature?.id || signatureRecordId);
      setSignatureName(normalizedName);
      setSignatureImage(finalSignatureUrl);
      setInitialsImage(finalInitialsUrl || "");
      setSignatureDataUrl("");
      setInitialsDataUrl("");
      setSignatureNaturalWH({});
      setInitialsNaturalWH({});
      signatureCanvasRef.current?.clear?.();
      initialsCanvasRef.current?.clear?.();

      // Sign URLs immediately for display
      try {
        const signedSigUrl = await getSignedSignatureUrl(finalSignatureUrl);
        setSignedSignatureUrl(signedSigUrl);
      } catch (err) {
        console.error("Failed to sign signature URL after save", err);
        setSignedSignatureUrl(finalSignatureUrl);
      }

      if (finalInitialsUrl) {
        try {
          const signedInitUrl = await getSignedSignatureUrl(finalInitialsUrl);
          setSignedInitialsUrl(signedInitUrl);
        } catch (err) {
          console.error("Failed to sign initials URL after save", err);
          setSignedInitialsUrl(finalInitialsUrl);
        }
      }
      if (signatureUploadRef.current) {
        signatureUploadRef.current.value = "";
      }
      if (initialsUploadRef.current) {
        initialsUploadRef.current.value = "";
      }

      setSuccess(t("signature-saved-alert", { defaultValue: "Signature saved successfully." }));
    } catch (saveError) {
      console.error("Failed to save signature", saveError);
      setError(saveError?.message || t("something-went-wrong-mssg", { defaultValue: "Something went wrong." }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AppShell>
      <div className="dana-section-grid">
        <Card className="animate-fade-up">
          <div className="dana-section-head">
            <span className="dana-section-icon">
              <Icon name={meta.icon} />
            </span>
            <div>
              {modeKicker && <p className="ui-kicker">{modeKicker}</p>}
              <h1 className="ui-title dana-section-title">{modeTitle}</h1>
            </div>
          </div>
          <p className="ui-copy mt-3">{modeDescription}</p>
          {mode === "preferences" || mode === "users" ? (
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to="/preferences">
                <Button variant={mode === "preferences" ? "primary" : "ghost"}>
                  {t("new-ui.settings.global-configuration-label", { defaultValue: "Configuration" })}
                </Button>
              </Link>
              {canManageUsers ? (
                <Link to="/users">
                  <Button variant={mode === "users" ? "primary" : "ghost"}>
                    {t("sidebar.Settings-Children.Users", { defaultValue: "Users" })}
                  </Button>
                </Link>
              ) : null}
              {canManageTenantBranding ? (
                <Link to="/branding">
                  <Button variant={mode === "branding" ? "primary" : "ghost"}>
                    {t("new-ui.settings.app-branding.title", { defaultValue: "App Branding" })}
                  </Button>
                </Link>
              ) : null}
            </div>
          ) : null}
          {mode === "branding" ? (
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to="/preferences">
                <Button variant="ghost">
                  {t("new-ui.settings.global-configuration-label", { defaultValue: "Configuration" })}
                </Button>
              </Link>
              {canManageUsers ? (
                <Link to="/users">
                  <Button variant="ghost">
                    {t("sidebar.Settings-Children.Users", { defaultValue: "Users" })}
                  </Button>
                </Link>
              ) : null}
              <Button variant="primary" disabled>
                {t("new-ui.settings.app-branding.title", { defaultValue: "App Branding" })}
              </Button>
            </div>
          ) : null}
        </Card>

        {mode === "profile" ? (
          <Card className="animate-fade-up [animation-delay:50ms]">
            <form className="grid grid-cols-1 gap-3 md:grid-cols-2" onSubmit={handleSaveProfile}>
              <Input
                id="profile-name"
                label={t("name", { defaultValue: "Name" })}
                value={profile.name}
                onChange={(event) => setProfile((current) => ({ ...current, name: event.target.value }))}
                required
              />
              <Input
                id="profile-email"
                label={t("email", { defaultValue: "Email" })}
                value={profile.email}
                disabled
              />
              <Input
                id="profile-phone"
                label={t("phone", { defaultValue: "Phone" })}
                value={profile.phone}
                onChange={(event) => setProfile((current) => ({ ...current, phone: event.target.value }))}
              />
              <div className="w-full">
                <Input
                  id="profile-company"
                  label={t("company", { defaultValue: "Company" })}
                  value={profile.company}
                  readOnly
                  aria-readonly="true"
                  className="cursor-not-allowed border-dashed bg-app-sand text-app-ink-muted focus:border-app-line focus:ring-0"
                />
                <p className="mt-1 text-xs text-app-ink-muted">
                  {t("new-ui.settings.profile.company-readonly-hint", {
                    defaultValue: "Esta compañía corresponde a tu tenant y no se puede editar desde el perfil."
                  })}
                </p>
              </div>
              <Input
                id="profile-jobtitle"
                label={t("job-title", { defaultValue: "Job title" })}
                value={profile.jobTitle}
                onChange={(event) => setProfile((current) => ({ ...current, jobTitle: event.target.value }))}
              />
              <div className="w-full">
                <label
                  htmlFor="profile-language"
                  className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted"
                >
                  {t("language", { defaultValue: "Language" })}
                </label>
                <select
                  id="profile-language"
                  className="w-full rounded-xl border border-app-line bg-white px-3 py-2.5 text-sm text-app-ink shadow-soft outline-none transition focus:border-app-brand focus:ring-4 focus:ring-app-brand/15"
                  value={profile.language}
                  onChange={(event) =>
                    setProfile((current) => ({
                      ...current,
                      language: String(event.target.value || "es").toLowerCase()
                    }))
                  }
                >
                  <option value="es">Español</option>
                  <option value="en">English</option>
                  <option value="fr">Français</option>
                  <option value="de">Deutsch</option>
                  <option value="it">Italiano</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <Button type="submit" disabled={isLoading}>
                  {isLoading
                    ? t("loading", { defaultValue: "Loading..." })
                    : t("save", { defaultValue: "Save" })}
                </Button>
              </div>
            </form>
          </Card>
        ) : null}

        {mode === "preferences" || mode === "user-preferences" ? (
          <Card className="animate-fade-up [animation-delay:50ms]">
            {canAccessPreferencesPanel ? (
              <>
                <div className="mb-4 flex items-center gap-2 border-b border-app-line pb-2">
                  {mode === "user-preferences" ? (
                    <button
                      type="button"
                      onClick={() => setPreferencesTab("general")}
                      className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold ${
                        preferencesTab === "general"
                          ? "bg-white text-app-ink shadow-soft"
                          : "text-app-ink-muted hover:bg-white/70"
                      }`}
                    >
                      <Icon name="settings" />
                      {t("general", { defaultValue: "General" })}
                    </button>
                  ) : null}
                  {mode === "user-preferences" && canUsePersonalEmailTemplates ? (
                    <button
                      type="button"
                      onClick={() => setPreferencesTab("email")}
                      className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold ${
                        preferencesTab === "email"
                          ? "bg-white text-app-ink shadow-soft"
                          : "text-app-ink-muted hover:bg-white/70"
                      }`}
                    >
                      <Icon name="mail" />
                      {t("email", { defaultValue: "Email" })}
                    </button>
                  ) : null}
                  {mode === "preferences" && canManageTenantSignatureValidation ? (
                    <button
                      type="button"
                      onClick={() => setPreferencesTab("configuration")}
                      className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold ${
                        preferencesTab === "configuration"
                          ? "bg-white text-app-ink shadow-soft"
                          : "text-app-ink-muted hover:bg-white/70"
                      }`}
                    >
                      <Icon name="settings" />
                      {t("new-ui.settings.global-configuration-label", { defaultValue: "Configuration" })}
                    </button>
                  ) : null}
                  {mode === "preferences" && canManageTenantEmailTemplates ? (
                    <button
                      type="button"
                      onClick={() => setPreferencesTab("email")}
                      className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold ${
                        preferencesTab === "email"
                          ? "bg-white text-app-ink shadow-soft"
                          : "text-app-ink-muted hover:bg-white/70"
                      }`}
                    >
                      <Icon name="mail" />
                      {t("email", { defaultValue: "Email" })}
                    </button>
                  ) : null}
                </div>

                {preferencesTab === "general" ? (
                  <form className="grid grid-cols-1 gap-4" onSubmit={handleSavePreferences}>
                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                      <div className="rounded-xl border border-app-line bg-app-paper px-3 py-3">
                        <p className="m-0 text-sm font-semibold text-app-ink">
                          {t("allowed-signature-types", { defaultValue: "Allowed signature types" })}
                          <InfoHint text={t("allowed-signature-types-help.p1", { defaultValue: "" })} />
                        </p>
                        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                          {preferences.signatureTypes.map((item) => {
                            const label =
                              item.name === "typed"
                                ? t("type", { defaultValue: "Type" })
                                : item.name === "default"
                                  ? t("default-value", { defaultValue: "Default" })
                                  : t(item.name, {
                                      defaultValue:
                                        item.name.charAt(0).toUpperCase() + item.name.slice(1)
                                    });

                            return (
                              <label key={item.name} className="inline-flex items-center gap-2 text-sm text-app-ink">
                                <input
                                  type="checkbox"
                                  checked={item.enabled}
                                  onChange={() => toggleSignatureType(item.name)}
                                />
                                {label}
                              </label>
                            );
                          })}
                        </div>
                      </div>

                      <div className="rounded-xl border border-app-line bg-app-paper px-3 py-3">
                        <p className="m-0 text-sm font-semibold text-app-ink">
                          {t("notify-on-signatures", { defaultValue: "Notify on signatures" })}
                          <InfoHint text={t("notify-on-signatures-help.p1", { defaultValue: "" })} />
                        </p>
                        <div className="mt-2 flex flex-wrap gap-5 text-sm text-app-ink">
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="radio"
                              checked={preferences.notifyOnSignatures === true}
                              onChange={() =>
                                setPreferences((current) => ({ ...current, notifyOnSignatures: true }))
                              }
                            />
                            {t("yes", { defaultValue: "Yes" })}
                          </label>
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="radio"
                              checked={preferences.notifyOnSignatures === false}
                              onChange={() =>
                                setPreferences((current) => ({ ...current, notifyOnSignatures: false }))
                              }
                            />
                            {t("no", { defaultValue: "No" })}
                          </label>
                        </div>
                      </div>

                      <div className="rounded-xl border border-app-line bg-app-paper px-3 py-3">
                        <p className="m-0 text-sm font-semibold text-app-ink">
                          {t("send-in-order", { defaultValue: "Send in order" })}
                          <InfoHint text={t("send-in-order-help.p1", { defaultValue: "" })} />
                        </p>
                        <div className="mt-2 flex flex-wrap gap-5 text-sm text-app-ink">
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="radio"
                              checked={preferences.sendInOrder === true}
                              onChange={() =>
                                setPreferences((current) => ({ ...current, sendInOrder: true }))
                              }
                            />
                            {t("yes", { defaultValue: "Yes" })}
                          </label>
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="radio"
                              checked={preferences.sendInOrder === false}
                              onChange={() =>
                                setPreferences((current) => ({ ...current, sendInOrder: false }))
                              }
                            />
                            {t("no", { defaultValue: "No" })}
                          </label>
                        </div>
                      </div>

                      <div className="rounded-xl border border-app-line bg-app-paper px-3 py-3">
                        <p className="m-0 text-sm font-semibold text-app-ink">
                          {t("isenable-otp", { defaultValue: "Enable OTP verification" })}
                          <InfoHint text={t("isenable-otp-help.p1", { defaultValue: "" })} />
                        </p>
                        <div className="mt-2 flex flex-wrap gap-5 text-sm text-app-ink">
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="radio"
                              checked={preferences.enableOtp === true}
                              onChange={() =>
                                setPreferences((current) => ({ ...current, enableOtp: true }))
                              }
                            />
                            {t("yes", { defaultValue: "Yes" })}
                          </label>
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="radio"
                              checked={preferences.enableOtp === false}
                              onChange={() =>
                                setPreferences((current) => ({ ...current, enableOtp: false }))
                              }
                            />
                            {t("no", { defaultValue: "No" })}
                          </label>
                        </div>
                      </div>

                      <div className="rounded-xl border border-app-line bg-app-paper px-3 py-3">
                        <p className="m-0 text-sm font-semibold text-app-ink">
                          {t("enable-tour", { defaultValue: "Enable tour" })}
                          <InfoHint text={t("istourenabled-help.p1", { defaultValue: "" })} />
                        </p>
                        <div className="mt-2 flex flex-wrap gap-5 text-sm text-app-ink">
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="radio"
                              checked={preferences.isTourEnabled === true}
                              onChange={() =>
                                setPreferences((current) => ({ ...current, isTourEnabled: true }))
                              }
                            />
                            {t("yes", { defaultValue: "Yes" })}
                          </label>
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="radio"
                              checked={preferences.isTourEnabled === false}
                              onChange={() =>
                                setPreferences((current) => ({ ...current, isTourEnabled: false }))
                              }
                            />
                            {t("no", { defaultValue: "No" })}
                          </label>
                        </div>
                      </div>

                      <div className="rounded-xl border border-app-line bg-app-paper px-3 py-3">
                        <Input
                          id="preferences-default-time-to-complete"
                          type="number"
                          min={1}
                          label={t("new-ui.settings.default-time-to-complete.label", {
                            defaultValue: "Default completion term (days)"
                          })}
                          value={preferences.defaultTimeToCompleteDays}
                          onChange={(event) =>
                            setPreferences((current) => ({
                              ...current,
                              defaultTimeToCompleteDays: event.target.value
                            }))
                          }
                        />
                        <p className="mt-2 text-xs text-app-ink-muted">
                          {t("new-ui.settings.default-time-to-complete.help", {
                            defaultValue:
                              "This value is used as the default in request and template forms; users can still adjust it per document."
                          })}
                        </p>
                      </div>

                      <div className="rounded-xl border border-app-line bg-app-paper px-3 py-3">
                        <p className="m-0 text-sm font-semibold text-app-ink">
                          {t("new-ui.settings.automatic-reminders.label", { defaultValue: "Send signing reminders" })}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-5 text-sm text-app-ink">
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="radio"
                              checked={preferences.automaticReminders === true}
                              onChange={() =>
                                setPreferences((current) => ({ ...current, automaticReminders: true }))
                              }
                            />
                            {t("yes", { defaultValue: "Yes" })}
                          </label>
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="radio"
                              checked={preferences.automaticReminders === false}
                              onChange={() =>
                                setPreferences((current) => ({ ...current, automaticReminders: false }))
                              }
                            />
                            {t("no", { defaultValue: "No" })}
                          </label>
                        </div>
                        <p className="mt-2 text-xs text-app-ink-muted">
                          {t("new-ui.settings.automatic-reminders.help", {
                            defaultValue: "Automatically send email reminders to signers who have not signed."
                          })}
                        </p>
                      </div>

                      {preferences.automaticReminders ? (
                        <div className="rounded-xl border border-app-line bg-app-paper px-3 py-3">
                          <Input
                            id="preferences-remind-once-in-every"
                            type="number"
                            min={1}
                            max={30}
                            label={t("new-ui.settings.remind-once-in-every.label", {
                              defaultValue: "Remind once every (days)"
                            })}
                            value={preferences.remindOnceInEvery}
                            onChange={(event) =>
                              setPreferences((current) => ({
                                ...current,
                                remindOnceInEvery: event.target.value
                              }))
                            }
                          />
                          <p className="mt-2 text-xs text-app-ink-muted">
                            {t("new-ui.settings.remind-once-in-every.help", {
                              defaultValue: "Number of days between each reminder email."
                            })}
                          </p>
                        </div>
                      ) : null}

                    </div>

                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                      <div className="w-full">
                        <label
                          htmlFor="preferences-timezone"
                          className="mb-1.5 block text-sm font-semibold text-app-ink"
                        >
                          {t("select-timezone", { defaultValue: "Timezone" })}
                        </label>
                        <div className="relative">
                          <select
                            id="preferences-timezone"
                            className="w-full appearance-none rounded-xl border border-app-line bg-white px-3 py-2.5 pr-10 text-sm text-app-ink shadow-soft outline-none transition focus:border-app-brand focus:ring-4 focus:ring-app-brand/15"
                            value={preferences.timezone}
                            onChange={(event) =>
                              setPreferences((current) => ({
                                ...current,
                                timezone: event.target.value
                              }))
                            }
                          >
                            {timezoneOptions.map((timezone) => (
                              <option key={timezone} value={timezone}>
                                {timezone}
                              </option>
                            ))}
                          </select>
                          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-app-ink-muted">
                            <Icon name="chevronDown" />
                          </span>
                        </div>
                      </div>
                      <div className="w-full">
                        <label
                          htmlFor="preferences-date-format"
                          className="mb-1.5 block text-sm font-semibold text-app-ink"
                        >
                          {t("date-format", { defaultValue: "Date format" })}
                        </label>
                        <select
                          id="preferences-date-format"
                          className="w-full rounded-xl border border-app-line bg-white px-3 py-2.5 text-sm text-app-ink shadow-soft outline-none transition focus:border-app-brand focus:ring-4 focus:ring-app-brand/15"
                          value={preferences.dateFormat}
                          onChange={(event) =>
                            setPreferences((current) => ({
                              ...current,
                              dateFormat: event.target.value
                            }))
                          }
                        >
                          {DATE_FORMAT_OPTIONS.map((format) => (
                            <option key={format} value={format}>
                              {format}
                            </option>
                          ))}
                        </select>
                        <div className="mt-2 flex flex-wrap gap-5 text-sm text-app-ink">
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="radio"
                              checked={preferences.is12HourTime === true}
                              onChange={() =>
                                setPreferences((current) => ({ ...current, is12HourTime: true }))
                              }
                            />
                            12 hr
                          </label>
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="radio"
                              checked={preferences.is12HourTime === false}
                              onChange={() =>
                                setPreferences((current) => ({ ...current, is12HourTime: false }))
                              }
                            />
                            24 hr
                          </label>
                        </div>
                        <p className="mt-2 text-sm font-semibold text-app-ink">
                          {formatDatePreview(preferences.timezone, preferences.is12HourTime)}
                        </p>
                      </div>
                    </div>

                    <div>
                      <Button type="submit" disabled={isLoading}>
                        {isLoading
                          ? t("loading", { defaultValue: "Loading..." })
                          : t("save", { defaultValue: "Save" })}
                      </Button>
                    </div>
                  </form>
                ) : mode === "user-preferences" && preferencesTab === "email" && canUsePersonalEmailTemplates ? (
                  <form className="grid grid-cols-1 gap-4" onSubmit={handleSavePersonalRequestEmailTemplate}>
                    <div className="rounded-xl border border-app-line bg-app-paper px-3 py-3">
                      <p className="m-0 text-sm font-semibold text-app-ink">
                        {t("new-ui.settings.personal-email.default-send-mode", {
                          defaultValue: "Default send mode"
                        })}
                      </p>
                      <p className="mt-1 text-xs text-app-ink-muted">
                        {t("new-ui.settings.personal-email.default-send-mode-help", {
                          defaultValue:
                            "Choose which template is selected by default when you send a signature request."
                        })}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-5 text-sm text-app-ink">
                        <label className="inline-flex items-center gap-2">
                          <input
                            type="radio"
                            checked={usePersonalRequestEmailByDefault === false}
                            onChange={() => setUsePersonalRequestEmailByDefault(false)}
                          />
                          {t("new-ui.settings.personal-email.use-corporate", {
                            defaultValue: "Use corporate template"
                          })}
                        </label>
                        <label className="inline-flex items-center gap-2">
                          <input
                            type="radio"
                            checked={usePersonalRequestEmailByDefault === true}
                            onChange={() => setUsePersonalRequestEmailByDefault(true)}
                          />
                          {t("new-ui.settings.personal-email.use-personal", {
                            defaultValue: "Use my personal template"
                          })}
                        </label>
                      </div>
                    </div>

                    <div className="rounded-xl border border-app-line bg-app-paper px-3 py-3">
                      <p className="m-0 text-sm font-semibold text-app-ink">
                        {t("request-email", { defaultValue: "Request Email" })}
                      </p>
                      <div className="mt-2 grid grid-cols-1 gap-3">
                          <Input
                            id="personal-request-email-subject"
                            label={t("subject", { defaultValue: "Subject" })}
                            value={personalRequestSubject}
                            onChange={(event) => setPersonalRequestSubject(event.target.value)}
                          />
                          <div className="w-full rounded-2xl border border-app-line bg-white px-3 py-3 shadow-soft">
                            <label
                              htmlFor="personal-request-email-body"
                              className="block text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted"
                            >
                              {t("body", { defaultValue: "Body" })}
                            </label>
                            <div className="mb-2 mt-2 flex flex-wrap items-center gap-2">
                              <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
                                {t("new-ui.settings.email.mode-label", { defaultValue: "Format" })}
                              </p>
                              <div className="inline-flex w-fit rounded-xl border border-app-line bg-white p-1">
                                <button
                                  type="button"
                                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                                    personalRequestBodyMode === EMAIL_TEMPLATE_MODE.HTML
                                      ? "bg-app-brand text-white"
                                      : "text-app-ink-muted hover:text-app-ink"
                                  }`}
                                  onClick={() =>
                                    handleTemplateModeChange("personal-request", EMAIL_TEMPLATE_MODE.HTML)
                                  }
                                >
                                  {t("new-ui.settings.email.mode-html", { defaultValue: "HTML" })}
                                </button>
                                <button
                                  type="button"
                                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                                    personalRequestBodyMode === EMAIL_TEMPLATE_MODE.PLAIN
                                      ? "bg-app-brand text-white"
                                      : "text-app-ink-muted hover:text-app-ink"
                                  }`}
                                  onClick={() =>
                                    handleTemplateModeChange("personal-request", EMAIL_TEMPLATE_MODE.PLAIN)
                                  }
                                >
                                  {t("new-ui.settings.email.mode-plain", { defaultValue: "Plain text" })}
                                </button>
                              </div>
                              <div className="ml-auto flex min-w-0 items-center gap-2">
                                <div className="flex items-center gap-1">
                                  <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
                                    {t("new-ui.settings.email.variables.title", {
                                      defaultValue: "Insert variables"
                                    })}
                                  </p>
                                  <InfoHint
                                    text={t("new-ui.settings.email.variables.hint", {
                                      defaultValue: "Click or drag into the body."
                                    })}
                                  />
                                </div>
                                <div className="flex max-w-full flex-nowrap items-center gap-1.5 overflow-x-auto pb-1">
                                  {emailVariableButtons.map((item) => (
                                    <button
                                      key={`personal-request-variable-${item.token}`}
                                      type="button"
                                      draggable
                                      className="whitespace-nowrap rounded-lg border border-app-line bg-white px-2 py-1 text-[11px] font-medium text-app-accent transition hover:border-app-accent/40 hover:bg-app-accent-soft"
                                      onClick={() => insertTemplateVariable("personal-request", item.token)}
                                      onDragStart={handleVariableDragStart(item.token)}
                                      title={item.token}
                                    >
                                      {item.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                            {personalRequestBodyMode === EMAIL_TEMPLATE_MODE.PLAIN ? (
                              <textarea
                                id="personal-request-email-body"
                                ref={personalRequestPlainBodyRef}
                                className="min-h-[180px] w-full rounded-xl border border-app-line bg-white px-3 py-2.5 text-sm text-app-ink shadow-soft outline-none transition focus:border-app-brand focus:ring-4 focus:ring-app-brand/15"
                                value={personalRequestBody}
                                onChange={(event) => setPersonalRequestBody(event.target.value)}
                                onDragOver={(event) => event.preventDefault()}
                                onDrop={handleVariableDrop("personal-request")}
                                placeholder={t("new-ui.settings.email.body-placeholder", {
                                  defaultValue: "Write your request email body..."
                                })}
                              />
                            ) : (
                              <div
                                className="dana-rich-editor"
                                onDragOver={(event) => event.preventDefault()}
                                onDrop={handleVariableDrop("personal-request")}
                              >
                                <ReactQuill
                                  id="personal-request-email-body"
                                  ref={personalRequestEmailEditorRef}
                                  theme="snow"
                                  value={personalRequestBody}
                                  onChange={setPersonalRequestBody}
                                  modules={EMAIL_EDITOR_MODULES}
                                  formats={EMAIL_EDITOR_FORMATS}
                                  placeholder={t("new-ui.settings.email.body-placeholder", {
                                    defaultValue: "Write your request email body..."
                                  })}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      }
                    </div>

                    <div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="submit"
                          disabled={
                            isLoading ||
                            !String(personalRequestSubject || "").trim() ||
                            isTemplateBodyEmpty(personalRequestBodyMode, personalRequestBody)
                          }
                        >
                          {isLoading
                            ? t("loading", { defaultValue: "Loading..." })
                            : t("save", { defaultValue: "Save" })}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          disabled={isLoading}
                          onClick={() => openEmailTemplatePreview("personal-request")}
                        >
                          {t("new-ui.settings.email.preview", { defaultValue: "Preview" })}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          disabled={isLoading}
                          onClick={() => openTestEmailModal("personal-request")}
                        >
                          {t("new-ui.settings.email.send-test", {
                            defaultValue: "Enviar correo de prueba"
                          })}
                        </Button>
                      </div>
                    </div>
                  </form>
                ) : mode === "preferences" && preferencesTab === "configuration" ? (
                  canManageTenantSignatureValidation ? (
                    <div className="grid grid-cols-1 gap-4">
                      <div className="rounded-xl border border-app-line bg-app-paper px-3 py-3">
                        <p className="m-0 text-sm font-semibold text-app-ink">
                          {t("signature-validation", { defaultValue: "Signature validation" })}
                          <InfoHint text={t("signature-threshold-help", { defaultValue: "" })} />
                        </p>
                        <p className="mt-1 text-xs text-app-ink-muted">
                          {t("new-ui.settings.signature-validation.scope-account", {
                            defaultValue: "This setting applies to all users in your account."
                          })}
                        </p>
                        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <Input
                            id="tenant-min-signature-area"
                            type="number"
                            label={t("min-signature-area", { defaultValue: "Minimum signature area (px2)" })}
                            value={tenantSignatureValidation.minSignatureArea}
                            onChange={(event) =>
                              setTenantSignatureValidation((current) => ({
                                ...current,
                                minSignatureArea: event.target.value
                              }))
                            }
                          />
                          <Input
                            id="tenant-min-signature-length"
                            type="number"
                            label={t("min-signature-length", { defaultValue: "Minimum stroke length (px)" })}
                            value={tenantSignatureValidation.minSignatureLength}
                            onChange={(event) =>
                              setTenantSignatureValidation((current) => ({
                                ...current,
                                minSignatureLength: event.target.value
                              }))
                            }
                          />
                        </div>
                        <div className="mt-3">
                          <Button type="button" disabled={isLoading} onClick={handleSaveTenantSignatureValidation}>
                            {isLoading
                              ? t("loading", { defaultValue: "Loading..." })
                              : t("save", { defaultValue: "Save" })}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : null
                ) : mode === "preferences" && preferencesTab === "email" && canManageTenantEmailTemplates ? (
                  <div className="grid grid-cols-1 gap-4">
                    {canManagePersonalEmailPermission ? (
                      <div className="rounded-xl border border-app-line bg-app-paper px-3 py-3">
                        <p className="m-0 text-sm font-semibold text-app-ink">
                          {t("new-ui.settings.personal-email.account-permission", {
                            defaultValue: "Allow personal email templates by role"
                          })}
                        </p>
                        <p className="mt-1 text-xs text-app-ink-muted">
                          {t("new-ui.settings.personal-email.account-permission-help", {
                            defaultValue:
                              "Choose which roles can configure personal request email templates and decide whether to send with the corporate or personal template."
                          })}
                        </p>
                        <div className="mt-3 grid grid-cols-1 gap-3 text-sm text-app-ink">
                          <div className="rounded-lg border border-app-line bg-white px-3 py-2">
                            <p className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
                              {t("new-ui.settings.personal-email.account-permission.admin-orgadmin", {
                                defaultValue: "Admin and OrgAdmin"
                              })}
                            </p>
                            <div className="mt-2 flex flex-wrap gap-5">
                              <label className="inline-flex items-center gap-2">
                                <input
                                  type="radio"
                                  checked={allowPersonalEmailTemplatesForAdmin === true}
                                  onChange={() => setAllowPersonalEmailTemplatesForAdmin(true)}
                                />
                                {t("yes", { defaultValue: "Yes" })}
                              </label>
                              <label className="inline-flex items-center gap-2">
                                <input
                                  type="radio"
                                  checked={allowPersonalEmailTemplatesForAdmin === false}
                                  onChange={() => setAllowPersonalEmailTemplatesForAdmin(false)}
                                />
                                {t("no", { defaultValue: "No" })}
                              </label>
                            </div>
                          </div>
                          <div className="rounded-lg border border-app-line bg-white px-3 py-2">
                            <p className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
                              {t("new-ui.settings.personal-email.account-permission.editor", {
                                defaultValue: "Editor"
                              })}
                            </p>
                            <div className="mt-2 flex flex-wrap gap-5">
                              <label className="inline-flex items-center gap-2">
                                <input
                                  type="radio"
                                  checked={allowPersonalEmailTemplatesForEditor === true}
                                  onChange={() => setAllowPersonalEmailTemplatesForEditor(true)}
                                />
                                {t("yes", { defaultValue: "Yes" })}
                              </label>
                              <label className="inline-flex items-center gap-2">
                                <input
                                  type="radio"
                                  checked={allowPersonalEmailTemplatesForEditor === false}
                                  onChange={() => setAllowPersonalEmailTemplatesForEditor(false)}
                                />
                                {t("no", { defaultValue: "No" })}
                              </label>
                            </div>
                          </div>
                          <div className="rounded-lg border border-app-line bg-white px-3 py-2">
                            <p className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
                              {t("new-ui.settings.personal-email.account-permission.user", {
                                defaultValue: "User"
                              })}
                            </p>
                            <div className="mt-2 flex flex-wrap gap-5">
                              <label className="inline-flex items-center gap-2">
                                <input
                                  type="radio"
                                  checked={allowPersonalEmailTemplatesForUser === true}
                                  onChange={() => setAllowPersonalEmailTemplatesForUser(true)}
                                />
                                {t("yes", { defaultValue: "Yes" })}
                              </label>
                              <label className="inline-flex items-center gap-2">
                                <input
                                  type="radio"
                                  checked={allowPersonalEmailTemplatesForUser === false}
                                  onChange={() => setAllowPersonalEmailTemplatesForUser(false)}
                                />
                                {t("no", { defaultValue: "No" })}
                              </label>
                            </div>
                          </div>
                        </div>
                        <div className="mt-3">
                          <Button type="button" disabled={isLoading} onClick={handleSaveTenantPersonalEmailPermission}>
                            {isLoading
                              ? t("loading", { defaultValue: "Loading..." })
                              : t("save", { defaultValue: "Save" })}
                          </Button>
                        </div>
                      </div>
                    ) : null}

                    {canManageTenantBranding ? (
                      <div className="rounded-xl border border-app-line bg-app-paper px-3 py-3">
                        <p className="m-0 text-sm font-semibold text-app-ink">
                          {t("new-ui.settings.branding.title", { defaultValue: "Email branding" })}
                          <InfoHint
                            text={t("new-ui.settings.branding.help", {
                              defaultValue:
                                "Upload your company logo and define the corporate color used in email communications."
                            })}
                          />
                        </p>
                        <div className="mt-3 grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                          <div className="rounded-xl border border-app-line bg-white p-3">
                            <p className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
                              {t("new-ui.settings.branding.logo", { defaultValue: "Company logo" })}
                            </p>
                            <div className="mt-2 flex items-center gap-3">
                              <div className="h-12 w-40 overflow-hidden rounded-lg border border-app-line bg-app-paper p-2">
                                <img
                                  src={tenantLogoPreview || tenantLogoUrl || DEFAULT_TENANT_LOGO_URL}
                                  alt={t("new-ui.settings.branding.logo-preview", { defaultValue: "Tenant logo preview" })}
                                  className="h-full w-full object-contain"
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  disabled={isLoading}
                                  onClick={() => tenantLogoInputRef.current?.click()}
                                >
                                  {t("new-ui.settings.branding.upload-logo", { defaultValue: "Upload logo" })}
                                </Button>
                                {tenantLogoFile ? (
                                  <span className="truncate text-xs text-app-ink-muted" title={tenantLogoFile.name}>
                                    {tenantLogoFile.name}
                                  </span>
                                ) : null}
                              </div>
                              <input
                                ref={tenantLogoInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                className="hidden"
                                onChange={handleTenantLogoSelection}
                              />
                            </div>
                          </div>

                          <div className="rounded-xl border border-app-line bg-white p-3">
                            <label
                              htmlFor="tenant-brand-color"
                              className="m-0 block text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted"
                            >
                              {t("new-ui.settings.branding.primary-color", { defaultValue: "Corporate color" })}
                            </label>
                            <div className="mt-2 flex items-center gap-3">
                              <input
                                id="tenant-brand-color"
                                type="color"
                                value={normalizeHexColor(tenantPrimaryColor)}
                                onChange={(event) => setTenantPrimaryColor(event.target.value)}
                                className="h-10 w-14 cursor-pointer rounded-lg border border-app-line bg-white p-1"
                              />
                              <input
                                type="text"
                                value={tenantPrimaryColor}
                                onChange={(event) => setTenantPrimaryColor(event.target.value)}
                                className="w-full rounded-xl border border-app-line bg-white px-3 py-2 text-sm text-app-ink outline-none transition focus:border-app-brand focus:ring-4 focus:ring-app-brand/15"
                                placeholder="#ef4b26"
                              />
                            </div>
                            <p className="mt-2 text-xs text-app-ink-muted">
                              {t("new-ui.settings.branding.primary-color-help", {
                                defaultValue: "Use HEX format (for example: #ef4b26)."
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="mt-3">
                          <Button type="button" disabled={isLoading} onClick={handleSaveTenantBranding}>
                            {isLoading
                              ? t("loading", { defaultValue: "Loading..." })
                              : t("save", { defaultValue: "Save" })}
                          </Button>
                        </div>
                      </div>
                    ) : null}

                    <div className="rounded-xl border border-app-line bg-app-paper px-3 py-3">
                      <p className="m-0 text-sm font-semibold text-app-ink">
                        {t("new-ui.settings.email.default-request-email", {
                          defaultValue: "Default Request Email"
                        })}
                      </p>
                      <div className="mt-2 grid grid-cols-1 gap-3">
                        <Input
                          id="request-email-subject"
                          label={t("subject", { defaultValue: "Subject" })}
                          value={requestSubject}
                          onChange={(event) => setRequestSubject(event.target.value)}
                        />
                        <div className="w-full rounded-2xl border border-app-line bg-white px-3 py-3 shadow-soft">
                          <label
                            htmlFor="request-email-body"
                            className="block text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted"
                          >
                            {t("body", { defaultValue: "Body" })}
                          </label>
                          <div className="mb-2 mt-2 flex flex-wrap items-center gap-2">
                            <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
                              {t("new-ui.settings.email.mode-label", { defaultValue: "Format" })}
                            </p>
                            <div className="inline-flex w-fit rounded-xl border border-app-line bg-white p-1">
                              <button
                                type="button"
                                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                                  requestBodyMode === EMAIL_TEMPLATE_MODE.HTML
                                    ? "bg-app-brand text-white"
                                    : "text-app-ink-muted hover:text-app-ink"
                                }`}
                                onClick={() => handleTemplateModeChange("request", EMAIL_TEMPLATE_MODE.HTML)}
                              >
                                {t("new-ui.settings.email.mode-html", { defaultValue: "HTML" })}
                              </button>
                              <button
                                type="button"
                                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                                  requestBodyMode === EMAIL_TEMPLATE_MODE.PLAIN
                                    ? "bg-app-brand text-white"
                                    : "text-app-ink-muted hover:text-app-ink"
                                }`}
                                onClick={() => handleTemplateModeChange("request", EMAIL_TEMPLATE_MODE.PLAIN)}
                              >
                                {t("new-ui.settings.email.mode-plain", { defaultValue: "Plain text" })}
                              </button>
                            </div>
                            <div className="ml-auto flex min-w-0 items-center gap-2">
                              <div className="flex items-center gap-1">
                                <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
                                  {t("new-ui.settings.email.variables.title", {
                                    defaultValue: "Insert variables"
                                  })}
                                </p>
                                <InfoHint
                                  text={t("new-ui.settings.email.variables.hint", {
                                    defaultValue: "Click or drag into the body."
                                  })}
                                />
                              </div>
                              <div className="flex max-w-full flex-nowrap items-center gap-1.5 overflow-x-auto pb-1">
                                {emailVariableButtons.map((item) => (
                                  <button
                                    key={`request-variable-${item.token}`}
                                    type="button"
                                    draggable
                                    className="whitespace-nowrap rounded-lg border border-app-line bg-white px-2 py-1 text-[11px] font-medium text-app-accent transition hover:border-app-accent/40 hover:bg-app-accent-soft"
                                    onClick={() => insertTemplateVariable("request", item.token)}
                                    onDragStart={handleVariableDragStart(item.token)}
                                    title={item.token}
                                  >
                                    {item.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                          {requestBodyMode === EMAIL_TEMPLATE_MODE.PLAIN ? (
                            <textarea
                              id="request-email-body"
                              ref={requestPlainBodyRef}
                              className="min-h-[180px] w-full rounded-xl border border-app-line bg-white px-3 py-2.5 text-sm text-app-ink shadow-soft outline-none transition focus:border-app-brand focus:ring-4 focus:ring-app-brand/15"
                              value={requestBody}
                              onChange={(event) => setRequestBody(event.target.value)}
                              onDragOver={(event) => event.preventDefault()}
                              onDrop={handleVariableDrop("request")}
                              placeholder={t("new-ui.settings.email.body-placeholder", {
                                defaultValue: "Write your request email body..."
                              })}
                            />
                          ) : (
                            <div
                              className="dana-rich-editor"
                              onDragOver={(event) => event.preventDefault()}
                              onDrop={handleVariableDrop("request")}
                            >
                              <ReactQuill
                                id="request-email-body"
                                ref={requestEmailEditorRef}
                                theme="snow"
                                value={requestBody}
                                onChange={setRequestBody}
                                modules={EMAIL_EDITOR_MODULES}
                                formats={EMAIL_EDITOR_FORMATS}
                                placeholder={t("new-ui.settings.email.body-placeholder", {
                                  defaultValue: "Write your request email body..."
                                })}
                              />
                            </div>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            disabled={isLoading || !String(requestSubject || "").trim() || isTemplateBodyEmpty(requestBodyMode, requestBody)}
                            onClick={() => saveTenantEmailTemplate("request")}
                          >
                            {t("save", { defaultValue: "Save" })}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            disabled={isLoading}
                            onClick={() => openEmailTemplatePreview("request")}
                          >
                            {t("new-ui.settings.email.preview", { defaultValue: "Preview" })}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            disabled={isLoading}
                            onClick={() => openTestEmailModal("request")}
                          >
                            {t("new-ui.settings.email.send-test", { defaultValue: "Enviar correo de prueba" })}
                          </Button>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-xl border border-app-line bg-app-paper px-3 py-3">
                      <p className="m-0 text-sm font-semibold text-app-ink">
                        {t("new-ui.settings.email.default-completion-email", {
                          defaultValue: "Default Completion Email"
                        })}
                      </p>
                      <div className="mt-2 grid grid-cols-1 gap-3">
                        <Input
                          id="completion-email-subject"
                          label={t("subject", { defaultValue: "Subject" })}
                          value={completionSubject}
                          onChange={(event) => setCompletionSubject(event.target.value)}
                        />
                        <div className="w-full rounded-2xl border border-app-line bg-white px-3 py-3 shadow-soft">
                          <label
                            htmlFor="completion-email-body"
                            className="block text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted"
                          >
                            {t("body", { defaultValue: "Body" })}
                          </label>
                          <div className="mb-2 mt-2 flex flex-wrap items-center gap-2">
                            <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
                              {t("new-ui.settings.email.mode-label", { defaultValue: "Format" })}
                            </p>
                            <div className="inline-flex w-fit rounded-xl border border-app-line bg-white p-1">
                              <button
                                type="button"
                                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                                  completionBodyMode === EMAIL_TEMPLATE_MODE.HTML
                                    ? "bg-app-brand text-white"
                                    : "text-app-ink-muted hover:text-app-ink"
                                }`}
                                onClick={() => handleTemplateModeChange("completion", EMAIL_TEMPLATE_MODE.HTML)}
                              >
                                {t("new-ui.settings.email.mode-html", { defaultValue: "HTML" })}
                              </button>
                              <button
                                type="button"
                                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                                  completionBodyMode === EMAIL_TEMPLATE_MODE.PLAIN
                                    ? "bg-app-brand text-white"
                                    : "text-app-ink-muted hover:text-app-ink"
                                }`}
                                onClick={() => handleTemplateModeChange("completion", EMAIL_TEMPLATE_MODE.PLAIN)}
                              >
                                {t("new-ui.settings.email.mode-plain", { defaultValue: "Plain text" })}
                              </button>
                            </div>
                            <div className="ml-auto flex min-w-0 items-center gap-2">
                              <div className="flex items-center gap-1">
                                <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
                                  {t("new-ui.settings.email.variables.title", {
                                    defaultValue: "Insert variables"
                                  })}
                                </p>
                                <InfoHint
                                  text={t("new-ui.settings.email.variables.hint", {
                                    defaultValue: "Click or drag into the body."
                                  })}
                                />
                              </div>
                              <div className="flex max-w-full flex-nowrap items-center gap-1.5 overflow-x-auto pb-1">
                                {emailVariableButtons.map((item) => (
                                  <button
                                    key={`completion-variable-${item.token}`}
                                    type="button"
                                    draggable
                                    className="whitespace-nowrap rounded-lg border border-app-line bg-white px-2 py-1 text-[11px] font-medium text-app-accent transition hover:border-app-accent/40 hover:bg-app-accent-soft"
                                    onClick={() => insertTemplateVariable("completion", item.token)}
                                    onDragStart={handleVariableDragStart(item.token)}
                                    title={item.token}
                                  >
                                    {item.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                          {completionBodyMode === EMAIL_TEMPLATE_MODE.PLAIN ? (
                            <textarea
                              id="completion-email-body"
                              ref={completionPlainBodyRef}
                              className="min-h-[180px] w-full rounded-xl border border-app-line bg-white px-3 py-2.5 text-sm text-app-ink shadow-soft outline-none transition focus:border-app-brand focus:ring-4 focus:ring-app-brand/15"
                              value={completionBody}
                              onChange={(event) => setCompletionBody(event.target.value)}
                              onDragOver={(event) => event.preventDefault()}
                              onDrop={handleVariableDrop("completion")}
                              placeholder={t("new-ui.settings.email.body-placeholder", {
                                defaultValue: "Write your completion email body..."
                              })}
                            />
                          ) : (
                            <div
                              className="dana-rich-editor"
                              onDragOver={(event) => event.preventDefault()}
                              onDrop={handleVariableDrop("completion")}
                            >
                              <ReactQuill
                                id="completion-email-body"
                                ref={completionEmailEditorRef}
                                theme="snow"
                                value={completionBody}
                                onChange={setCompletionBody}
                                modules={EMAIL_EDITOR_MODULES}
                                formats={EMAIL_EDITOR_FORMATS}
                                placeholder={t("new-ui.settings.email.body-placeholder", {
                                  defaultValue: "Write your completion email body..."
                                })}
                              />
                            </div>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            disabled={isLoading || !String(completionSubject || "").trim() || isTemplateBodyEmpty(completionBodyMode, completionBody)}
                            onClick={() => saveTenantEmailTemplate("completion")}
                          >
                            {t("save", { defaultValue: "Save" })}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            disabled={isLoading}
                            onClick={() => openEmailTemplatePreview("completion")}
                          >
                            {t("new-ui.settings.email.preview", { defaultValue: "Preview" })}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            disabled={isLoading}
                            onClick={() => openTestEmailModal("completion")}
                          >
                            {t("new-ui.settings.email.send-test", { defaultValue: "Enviar correo de prueba" })}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </>
            ) : (
              <p className="dana-muted">
                {t("something-went-wrong-mssg", {
                  defaultValue: "You do not have permission to access this section."
                })}
              </p>
            )}
          </Card>
        ) : null}

        {mode === "password" ? (
          <Card className="animate-fade-up [animation-delay:50ms]">
            <form className="grid grid-cols-1 gap-3 md:max-w-[520px]" onSubmit={handleChangePassword}>
              <Input
                id="password-current"
                type="password"
                label={t("current-password", { defaultValue: "Current password" })}
                value={passwordForm.currentPassword}
                onChange={(event) =>
                  setPasswordForm((current) => ({ ...current, currentPassword: event.target.value }))
                }
                required
              />
              <Input
                id="password-new"
                type="password"
                label={t("new-password", { defaultValue: "New password" })}
                value={passwordForm.newPassword}
                onChange={(event) =>
                  setPasswordForm((current) => ({ ...current, newPassword: event.target.value }))
                }
                required
              />
              <Input
                id="password-confirm"
                type="password"
                label={t("confirm-password", { defaultValue: "Confirm password" })}
                value={passwordForm.confirmPassword}
                onChange={(event) =>
                  setPasswordForm((current) => ({ ...current, confirmPassword: event.target.value }))
                }
                required
              />
              <div>
                <Button type="submit" disabled={isLoading}>
                  {isLoading
                    ? t("loading", { defaultValue: "Loading..." })
                    : t("change-password", { defaultValue: "Change password" })}
                </Button>
              </div>
            </form>
          </Card>
        ) : null}

        {mode === "users" ? (
          <Card className="animate-fade-up [animation-delay:50ms]">
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="m-0 text-sm text-app-ink-muted">
                {t("users-from-teams", { defaultValue: "Users from your organization." })}
              </p>
              {canManageUsers ? (
                <Button
                  onClick={openAddUserModal}
                  className="bg-[#22c55e] text-white hover:bg-[#1ea34f] focus-visible:ring-[rgba(34,197,94,0.35)]"
                >
                  {t("add-user", { defaultValue: "Add user" })}
                </Button>
              ) : null}
            </div>
            {isLoading ? (
              <p className="dana-muted">{t("loading", { defaultValue: "Loading..." })}</p>
            ) : tenantUsers.length ? (
              <div className="overflow-x-auto rounded-xl border border-app-line bg-white shadow-soft">
                <table className="w-full table-fixed border-collapse text-left">
                  <thead className="bg-app-sand">
                    <tr>
                      <th className="w-[29%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                        {t("report-heading.Name", { defaultValue: "Name" })}
                      </th>
                      <th className="w-[27%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                        {t("report-heading.Email", { defaultValue: "Email" })}
                      </th>
                      <th className="w-[24%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                        {t("report-heading.Role", { defaultValue: "Role" })}
                      </th>
                      <th className="w-[10%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                        {t("report-heading.Active", { defaultValue: "Active" })}
                      </th>
                      <th className="w-[10%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                        {t("new-ui.settings.users.actions", { defaultValue: "Actions" })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenantUsers.map((user) => {
                      const roleDraft = String(
                        userRoleDraftById[user.objectId] || getRoleShortValue(user.rawRole)
                      ).trim();
                      const roleIsLoading = Boolean(isRoleUpdateLoadingById[user.objectId]);
                      const hasRoleChanges = `contracts_${roleDraft}` !== String(user.rawRole || "").trim();
                      const canSaveRole = canEditUserRole(user) && roleDraft && hasRoleChanges && !roleIsLoading;

                      return (
                        <tr key={user.objectId} className="border-t border-app-line">
                          <td className="truncate px-2 py-1.5 text-[12px] text-app-ink" title={user.name}>
                            {user.name}
                          </td>
                          <td className="truncate px-2 py-1.5 text-[11px] text-app-ink-muted" title={user.email}>
                            {user.email}
                          </td>
                          <td className="truncate px-2 py-1.5 text-[11px] text-app-ink-muted" title={user.role}>
                            {canEditUserRole(user) ? (
                              <div className="flex items-center gap-1">
                                <select
                                  className="min-w-[102px] rounded-md border border-app-line bg-white px-2 py-1 text-[11px] text-app-ink outline-none transition focus:border-app-brand focus:ring-2 focus:ring-app-brand/15"
                                  value={roleDraft}
                                  onChange={(event) => handleUserRoleDraftChange(user.objectId, event.target.value)}
                                  disabled={roleIsLoading || isToggleUserLoading || isDeleteUserLoading}
                                >
                                  {USER_ROLE_EDIT_OPTIONS.map((roleOption) => (
                                    <option key={roleOption.raw} value={roleOption.value}>
                                      {roleOption.label}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  type="button"
                                  onClick={() => handleSaveUserRole(user)}
                                  disabled={!canSaveRole || isToggleUserLoading || isDeleteUserLoading}
                                  className="inline-flex items-center rounded-md border border-app-line bg-app-paper px-2 py-1 text-[11px] font-semibold text-app-ink transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-55"
                                >
                                  {roleIsLoading
                                    ? t("loading", { defaultValue: "Loading..." })
                                    : t("save", { defaultValue: "Save" })}
                                </button>
                              </div>
                            ) : (
                              user.role
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">
                            {canManageUsers && canEditUserStatus(user) ? (
                              <button
                                type="button"
                                role="switch"
                                aria-checked={user.active}
                                onClick={() => openToggleUserModal(user)}
                                disabled={isToggleUserLoading || isDeleteUserLoading || roleIsLoading}
                                className={`relative inline-flex h-5 w-10 items-center rounded-full transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-app-brand/20 ${
                                  user.active ? "bg-emerald-500" : "bg-slate-300"
                                }`}
                                title={
                                  user.active
                                    ? t("deactivate", { defaultValue: "Deactivate" })
                                    : t("activate", { defaultValue: "Activate" })
                                }
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                                    user.active ? "translate-x-5" : "translate-x-1"
                                  }`}
                                />
                              </button>
                            ) : (
                              <span>
                                {user.active ? t("yes", { defaultValue: "Yes" }) : t("no", { defaultValue: "No" })}
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">
                            {canManageUsers && canEditUserStatus(user) ? (
                              <button
                                type="button"
                                onClick={() => openDeleteUserModal(user)}
                                disabled={isToggleUserLoading || isDeleteUserLoading || roleIsLoading}
                                className="inline-flex items-center gap-1 text-app-danger hover:underline"
                                title={t("delete-user", { defaultValue: "Delete user" })}
                              >
                                <Icon name="trash" className="h-3.5 w-3.5" />
                                {t("delete", { defaultValue: "Delete" })}
                              </button>
                            ) : (
                              <span>-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="dana-muted">
                {t("new-ui.dashboard.recent-sent-documents.empty", {
                  defaultValue: "No users found."
                })}
              </p>
            )}
          </Card>
        ) : null}

        {mode === "branding" ? (
          <Card className="animate-fade-up [animation-delay:50ms]">
            {canManageTenantBranding ? (
              <BrandingSettings 
                tenantId={getExtUser()?.TenantId?.objectId || localStorage.getItem("TenantId")}
                isAdmin={isAdminUser}
              />
            ) : (
              <p className="text-sm text-app-ink-muted">
                {t("new-ui.settings.branding.access-denied", { 
                  defaultValue: "You do not have permission to manage branding settings." 
                })}
              </p>
            )}
          </Card>
        ) : null}

        {mode === "signature" ? (
          <Card className="animate-fade-up [animation-delay:50ms]">
            <form className="grid grid-cols-1 gap-4" onSubmit={handleSaveSignature}>
              <Input
                id="signature-name"
                label={t("name", { defaultValue: "Name" })}
                value={signatureName}
                onChange={(event) => setSignatureName(event.target.value)}
              />

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <div className="rounded-xl border border-app-line bg-app-paper p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="m-0 text-sm font-semibold text-app-ink">
                      {t("signature", { defaultValue: "Signature" })}
                    </p>
                    <p className="m-0 text-xs text-app-ink-muted">
                      {t("draw", { defaultValue: "Draw" })} / {t("upload", { defaultValue: "Upload" })}
                    </p>
                  </div>

                  <input
                    ref={signatureUploadRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleSignatureUpload}
                  />

                  <div
                    ref={signatureCanvasHostRef}
                    className="overflow-hidden rounded-xl border border-app-line bg-white"
                  >
                    {signatureImage ? (
                      <img
                        alt={t("signature", { defaultValue: "Signature" })}
                        src={signedSignatureUrl || signatureImage}
                        className="h-[180px] w-full object-contain"
                      />
                    ) : (
                      <SignatureCanvas
                        key={`signature-canvas-${signatureCanvasWidth}`}
                        ref={signatureCanvasRef}
                        penColor={penColor}
                        canvasProps={{
                          width: signatureCanvasWidth,
                          height: SIGNATURE_CANVAS_HEIGHT,
                          className: "block w-full cursor-crosshair",
                          style: { height: `${SIGNATURE_CANVAS_HEIGHT}px` }
                        }}
                        onEnd={handleSignatureDrawEnd}
                        dotSize={1}
                      />
                    )}
                  </div>

                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <div className="inline-flex items-center gap-1.5">
                      {SIGNATURE_COLOR_OPTIONS.map((colorOption) => (
                        <button
                          key={`signature-pen-${colorOption}`}
                          type="button"
                          onClick={() => setPenColor(colorOption)}
                          className={`h-5 w-5 rounded-full border transition ${
                            penColor === colorOption ? "border-app-brand shadow-soft" : "border-app-line"
                          }`}
                          style={{ backgroundColor: colorOption }}
                          aria-label={`${t("signature", { defaultValue: "Signature" })} color`}
                        />
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        className="px-3 py-1.5"
                        onClick={() => signatureUploadRef.current?.click?.()}
                      >
                        {t("upload", { defaultValue: "Upload" })}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        className="px-3 py-1.5"
                        onClick={clearSignatureInput}
                      >
                        {t("clear", { defaultValue: "Clear" })}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-app-line bg-app-paper p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="m-0 text-sm font-semibold text-app-ink">
                      {t("initials", { defaultValue: "Initials" })}
                    </p>
                    <div className="inline-flex gap-1">
                      {["draw", "type", "upload"].map((tab) => (
                        <button
                          key={tab}
                          type="button"
                          className={`rounded-lg px-2 py-0.5 text-xs font-medium transition ${
                            initialsMode === tab
                              ? "bg-app-brand text-white"
                              : "bg-transparent text-app-ink-muted hover:bg-gray-100"
                          }`}
                          onClick={() => {
                            setInitialsMode(tab);
                            if (tab === "upload") {
                              initialsUploadRef.current?.click?.();
                            }
                          }}
                        >
                          {tab === "draw"
                            ? t("draw", { defaultValue: "Draw" })
                            : tab === "type"
                              ? t("type", { defaultValue: "Type" })
                              : t("upload", { defaultValue: "Upload" })}
                        </button>
                      ))}
                    </div>
                  </div>

                  <input
                    ref={initialsUploadRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleInitialsUpload}
                  />

                  <div
                    ref={initialsCanvasHostRef}
                    className="overflow-hidden rounded-xl border border-app-line bg-white"
                  >
                    {initialsMode === "type" ? (
                      initialsDataUrl && initialsText ? (
                        <img
                          alt={t("initials", { defaultValue: "Initials" })}
                          src={initialsDataUrl}
                          className="h-[140px] w-full object-contain"
                        />
                      ) : (
                        <div className="flex h-[140px] items-center justify-center">
                          <p className="text-sm text-app-ink-muted">
                            {t("new-ui.settings.signature.type-initials-hint", { defaultValue: "Type your initials below" })}
                          </p>
                        </div>
                      )
                    ) : initialsImage ? (
                      <img
                        alt={t("initials", { defaultValue: "Initials" })}
                        src={signedInitialsUrl || initialsImage}
                        className="h-[140px] w-full object-contain"
                      />
                    ) : (
                      <SignatureCanvas
                        key={`initials-canvas-${initialsCanvasWidth}`}
                        ref={initialsCanvasRef}
                        penColor={initialsPenColor}
                        canvasProps={{
                          width: initialsCanvasWidth,
                          height: INITIALS_CANVAS_HEIGHT,
                          className: "block w-full cursor-crosshair",
                          style: { height: `${INITIALS_CANVAS_HEIGHT}px` }
                        }}
                        onEnd={handleInitialsDrawEnd}
                        dotSize={1}
                      />
                    )}
                  </div>

                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    {initialsMode === "type" ? (
                      <input
                        type="text"
                        maxLength={5}
                        value={initialsText}
                        onChange={(e) => generateInitialsFromText(e.target.value)}
                        placeholder={t("new-ui.settings.signature.type-initials-placeholder", { defaultValue: "e.g. AB" })}
                        className="flex-1 rounded-lg border border-app-line bg-white px-3 py-1.5 text-sm text-app-ink outline-none focus:border-app-brand"
                      />
                    ) : (
                      <div className="inline-flex items-center gap-1.5">
                        {SIGNATURE_COLOR_OPTIONS.map((colorOption) => (
                          <button
                            key={`initials-pen-${colorOption}`}
                            type="button"
                            onClick={() => setInitialsPenColor(colorOption)}
                            className={`h-5 w-5 rounded-full border transition ${
                              initialsPenColor === colorOption ? "border-app-brand shadow-soft" : "border-app-line"
                            }`}
                            style={{ backgroundColor: colorOption }}
                            aria-label={`${t("initials", { defaultValue: "Initials" })} color`}
                          />
                        ))}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        className="px-3 py-1.5"
                        onClick={clearInitialsInput}
                      >
                        {t("clear", { defaultValue: "Clear" })}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Button type="submit" disabled={isLoading}>
                  {isLoading
                    ? t("loading", { defaultValue: "Loading..." })
                    : t("save", { defaultValue: "Save" })}
                </Button>
              </div>
            </form>
          </Card>
        ) : null}

        {mode === "api" ? (
          canManageApiTokens ? (
            <Card className="animate-fade-up [animation-delay:50ms]">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="m-0 text-lg font-semibold text-app-ink">
                    {t("new-ui.settings.api.tokens-title", { defaultValue: "API tokens" })}
                  </h2>
                  <p className="m-0 mt-1 text-sm text-app-ink-muted">
                    {t("new-ui.settings.api.tokens-description", {
                      defaultValue:
                        "Generate and manage tokens for external integrations. A token value is shown only once when created."
                    })}
                  </p>
                </div>
                <Button
                  type="button"
                  onClick={openCreateApiTokenModal}
                  disabled={isApiTokenSubmitting || isApiTokensLoading}
                >
                  {t("new-ui.settings.api.generate-token", { defaultValue: "Generate token" })}
                </Button>
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <label className="inline-flex items-center gap-2 text-sm text-app-ink-muted">
                  <input
                    type="checkbox"
                    checked={showRevokedApiTokens}
                    onChange={(event) => setShowRevokedApiTokens(event.target.checked)}
                  />
                  {t("new-ui.settings.api.show-revoked", { defaultValue: "Show revoked tokens" })}
                </label>
              </div>

              {isApiTokensLoading ? (
                <div className="mt-6 rounded-xl border border-app-line bg-app-paper px-4 py-8 text-center text-sm text-app-ink-muted">
                  {t("loading", { defaultValue: "Loading..." })}
                </div>
              ) : filteredApiTokens.length === 0 ? (
                <div className="mt-6 rounded-xl border border-dashed border-app-line bg-white px-4 py-8 text-center text-sm text-app-ink-muted">
                  {showRevokedApiTokens
                    ? t("new-ui.settings.api.empty-all", { defaultValue: "There are no API tokens in this list." })
                    : t("new-ui.settings.api.empty-active", { defaultValue: "No active API tokens found." })}
                </div>
              ) : (
                <div className="mt-4 overflow-x-auto rounded-xl border border-app-line bg-white">
                  <table className="min-w-[840px] w-full text-left text-sm text-app-ink">
                    <thead className="bg-app-paper text-[11px] uppercase tracking-[0.08em] text-app-ink-muted">
                      <tr>
                        <th className="px-3 py-2 font-semibold">
                          {t("name", { defaultValue: "Name" })}
                        </th>
                        <th className="px-3 py-2 font-semibold">
                          {t("new-ui.settings.api.scopes", { defaultValue: "Scope" })}
                        </th>
                        <th className="px-3 py-2 font-semibold">
                          {t("new-ui.settings.api.created-at", { defaultValue: "Created" })}
                        </th>
                        <th className="px-3 py-2 font-semibold">
                          {t("new-ui.settings.api.last-used", { defaultValue: "Last used" })}
                        </th>
                        <th className="px-3 py-2 font-semibold">
                          {t("new-ui.settings.api.expires-at", { defaultValue: "Expires" })}
                        </th>
                        <th className="px-3 py-2 font-semibold text-center">
                          {t("status", { defaultValue: "Status" })}
                        </th>
                        <th className="px-3 py-2 font-semibold text-right">
                          {t("actions", { defaultValue: "Actions" })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredApiTokens.map((token) => {
                        const tokenScopes =
                          Array.isArray(token?.scopes) && token.scopes.length > 0 ? token.scopes : ["*"];
                        const tokenStatus = getApiTokenStatus(token);
                        const statusStyles =
                          tokenStatus === "revoked"
                            ? "border-red-300 bg-red-50 text-red-700"
                            : tokenStatus === "expired"
                              ? "border-amber-300 bg-amber-50 text-amber-700"
                              : "border-emerald-300 bg-emerald-50 text-emerald-700";
                        const statusLabel =
                          tokenStatus === "revoked"
                            ? t("new-ui.settings.api.status.revoked", { defaultValue: "Revoked" })
                            : tokenStatus === "expired"
                              ? t("new-ui.settings.api.status.expired", { defaultValue: "Expired" })
                              : t("new-ui.settings.api.status.active", { defaultValue: "Active" });

                        return (
                          <tr key={token?.objectId || token?.tokenId} className="border-t border-app-line">
                            <td className="px-3 py-2 align-top">
                              <div className="flex flex-col gap-1">
                                <span className="font-semibold text-app-ink">
                                  {token?.label || t("new-ui.settings.api.token-no-name", { defaultValue: "API token" })}
                                </span>
                                <span className="text-xs text-app-ink-muted">
                                  {t("new-ui.settings.api.prefix", {
                                    defaultValue: "Prefix: {{prefix}}",
                                    prefix: token?.tokenPrefix || "-"
                                  })}
                                </span>
                              </div>
                            </td>
                            <td className="px-3 py-2 align-top">
                              <div className="flex flex-wrap gap-1.5">
                                {tokenScopes.map((scope) => (
                                  <span
                                    key={`${token?.objectId || token?.tokenId}-${scope}`}
                                    className="inline-flex items-center rounded-full border border-app-line bg-app-paper px-2 py-0.5 text-[11px] font-semibold text-app-ink"
                                  >
                                    {scope === "*"
                                      ? t("new-ui.settings.api.scope-all", { defaultValue: "Full access" })
                                      : scope.replace(":", " · ")}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-3 py-2 align-top text-xs text-app-ink-muted">
                              {formatDateTimeValue(token?.createdAt)}
                            </td>
                            <td className="px-3 py-2 align-top text-xs text-app-ink-muted">
                              {formatDateTimeValue(token?.lastUsedAt)}
                            </td>
                            <td className="px-3 py-2 align-top text-xs text-app-ink-muted">
                              {formatDateTimeValue(token?.expiresAt)}
                            </td>
                            <td className="px-3 py-2 align-top text-center">
                              <span
                                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusStyles}`}
                              >
                                {statusLabel}
                              </span>
                            </td>
                            <td className="px-3 py-2 align-top">
                              <div className="flex justify-end gap-2">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  className="px-3 py-1.5 text-xs"
                                  onClick={() =>
                                    setRevealedApiToken({
                                      token: null,
                                      tokenInfo: token,
                                      sessionOnly: true
                                    })
                                  }
                                >
                                  {t("new-ui.settings.api.view-details", { defaultValue: "View details" })}
                                </Button>
                                {token?.revoked !== true ? (
                                  <Button
                                    type="button"
                                    variant="danger"
                                    className="px-3 py-1.5 text-xs"
                                    disabled={isApiTokenSubmitting}
                                    onClick={() => handleRevokeApiToken(token?.objectId)}
                                  >
                                    {t("new-ui.settings.api.revoke", { defaultValue: "Revoke" })}
                                  </Button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          ) : (
            <Card className="animate-fade-up [animation-delay:50ms]">
              <h2 className="m-0 text-lg font-semibold text-app-ink">
                {t("new-ui.settings.api.permission-title", { defaultValue: "Insufficient permissions" })}
              </h2>
              <p className="m-0 mt-2 text-sm text-app-ink-muted">
                {t("new-ui.settings.api.permission-body", {
                  defaultValue: "This section is only available for Admin and OrgAdmin users."
                })}
              </p>
            </Card>
          )
        ) : null}
      </div>
      <Modal
        isOpen={isEmailPreviewOpen}
        title={t("new-ui.settings.email.preview-title", { defaultValue: "Email preview" })}
        onClose={() => {
          setIsEmailPreviewOpen(false);
          setEmailPreviewIsPlain(false);
        }}
      >
        <div className="grid grid-cols-1 gap-3">
          <div className="rounded-xl border border-app-line bg-app-paper px-3 py-2">
            <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
              {t("subject", { defaultValue: "Subject" })}
            </p>
            <p className="m-0 mt-1 text-sm font-semibold text-app-ink">{emailPreviewSubject}</p>
          </div>
          {emailPreviewIsPlain ? (
            <div className="rounded-xl border border-app-line bg-white p-3">
              <pre className="m-0 whitespace-pre-wrap break-words text-sm text-app-ink">{emailPreviewHtml}</pre>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-app-line bg-white">
              <iframe
                title="email-preview"
                className="h-[460px] w-full border-0"
                sandbox=""
                srcDoc={emailPreviewHtml}
              />
            </div>
          )}
        </div>
      </Modal>
      <Modal
        isOpen={isTestEmailModalOpen}
        title={t("new-ui.settings.email.send-test-title", { defaultValue: "Enviar correo de prueba" })}
        onClose={() => {
          setIsTestEmailModalOpen(false);
          setTestEmailRecipient("");
        }}
      >
        <div className="grid grid-cols-1 gap-3">
          <p className="m-0 text-sm text-app-ink-muted">
            {t("new-ui.settings.email.send-test-description", {
              defaultValue: "Envía un correo de prueba para verificar que el logo, los textos y el color corporativo se muestran correctamente."
            })}
          </p>
          <Input
            id="test-email-recipient"
            type="email"
            label={t("new-ui.settings.email.recipient-email", { defaultValue: "Correo del destinatario" })}
            value={testEmailRecipient}
            onChange={(event) => setTestEmailRecipient(event.target.value)}
            placeholder={t("new-ui.settings.email.recipient-placeholder", { defaultValue: "correo@ejemplo.com" })}
            required
          />
          <div className="flex gap-2">
            <Button
              type="button"
              disabled={isSendingTestEmail || !testEmailRecipient.trim()}
              onClick={sendTestEmail}
            >
              {isSendingTestEmail
                ? t("loading", { defaultValue: "Loading..." })
                : t("new-ui.settings.email.send", { defaultValue: "Enviar" })}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={isSendingTestEmail}
              onClick={() => {
                setIsTestEmailModalOpen(false);
                setTestEmailRecipient("");
              }}
            >
              {t("cancel", { defaultValue: "Cancel" })}
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={isApiTokenCreateModalOpen}
        title={t("new-ui.settings.api.modal-create-title", { defaultValue: "Generate new token" })}
        onClose={closeCreateApiTokenModal}
      >
        <form className="grid grid-cols-1 gap-4" onSubmit={handleCreateApiToken}>
          <Input
            id="api-token-label"
            label={t("new-ui.settings.api.form-name", { defaultValue: "Token name" })}
            value={apiTokenForm.label}
            onChange={(event) =>
              setApiTokenForm((current) => ({
                ...current,
                label: event.target.value
              }))
            }
            placeholder={t("new-ui.settings.api.form-name-placeholder", {
              defaultValue: "Ex. CRM integration"
            })}
          />

          <div className="grid grid-cols-1 gap-2">
            <p className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
              {t("new-ui.settings.api.form-scopes", { defaultValue: "Token scopes" })}
            </p>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {AVAILABLE_API_SCOPES.map((scope) => (
                <label
                  key={scope.value}
                  className="inline-flex items-center gap-2 rounded-xl border border-app-line bg-app-paper px-3 py-2 text-sm text-app-ink"
                >
                  <input
                    type="checkbox"
                    checked={apiTokenForm.scopes.includes(scope.value)}
                    onChange={() => toggleApiTokenScope(scope.value)}
                  />
                  {t(`new-ui.settings.api.scope-label.${scope.value}`, {
                    defaultValue: scope.label
                  })}
                </label>
              ))}
            </div>
            <p className="m-0 text-xs text-app-ink-muted">
              {t("new-ui.settings.api.form-scopes-note", {
                defaultValue: "If no scope is selected, the token gets full access."
              })}
            </p>
          </div>

          <Input
            id="api-token-expiration"
            type="datetime-local"
            label={t("new-ui.settings.api.form-expiration", { defaultValue: "Expiration date (optional)" })}
            value={apiTokenForm.expiresAt}
            onChange={(event) =>
              setApiTokenForm((current) => ({
                ...current,
                expiresAt: event.target.value
              }))
            }
          />

          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" disabled={isApiTokenSubmitting}>
              {isApiTokenSubmitting
                ? t("loading", { defaultValue: "Loading..." })
                : t("new-ui.settings.api.modal-create-submit", { defaultValue: "Create token" })}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={closeCreateApiTokenModal}
              disabled={isApiTokenSubmitting}
            >
              {t("cancel", { defaultValue: "Cancel" })}
            </Button>
          </div>
        </form>
      </Modal>
      <Modal
        isOpen={Boolean(revealedApiToken)}
        title={t("new-ui.settings.api.modal-reveal-title", { defaultValue: "Token generated" })}
        onClose={() => setRevealedApiToken(null)}
      >
        <div className="grid grid-cols-1 gap-4">
          {revealedApiToken?.token ? (
            <>
              <p className="m-0 text-sm text-app-ink-muted">
                {t("new-ui.settings.api.modal-reveal-once", {
                  defaultValue: "Copy and store this token now. It will not be shown again."
                })}
              </p>
              <div className="rounded-xl border border-app-line bg-app-paper px-3 py-3">
                <code className="break-all text-sm text-app-ink">{revealedApiToken.token}</code>
              </div>
              <div>
                <Button type="button" variant="ghost" onClick={() => handleCopyApiToken(revealedApiToken.token)}>
                  {t("copy-to-clipboard", { defaultValue: "Copy to clipboard" })}
                </Button>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              <p className="m-0 text-sm text-app-ink-muted">
                {t("new-ui.settings.api.prefix", {
                  defaultValue: "Prefix: {{prefix}}",
                  prefix: revealedApiToken?.tokenInfo?.tokenPrefix || "-"
                })}
              </p>
              <div className="grid grid-cols-1 gap-2">
                <p className="m-0 text-sm font-semibold text-app-ink">
                  {t("new-ui.settings.api.scopes", { defaultValue: "Scope" })}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {(
                    Array.isArray(revealedApiToken?.tokenInfo?.scopes) &&
                    revealedApiToken.tokenInfo.scopes.length > 0
                      ? revealedApiToken.tokenInfo.scopes
                      : ["*"]
                  ).map((scope) => (
                    <span
                      key={scope}
                      className="inline-flex items-center rounded-full border border-app-line bg-app-paper px-2 py-0.5 text-[11px] font-semibold text-app-ink"
                    >
                      {scope === "*"
                        ? t("new-ui.settings.api.scope-all", { defaultValue: "Full access" })
                        : scope.replace(":", " · ")}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-end">
            <Button type="button" onClick={() => setRevealedApiToken(null)}>
              {t("close", { defaultValue: "Close" })}
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={isAddUserOpen}
        title={t("add-user", { defaultValue: "Add user" })}
        onClose={closeAddUserModal}
      >
        <form className="grid grid-cols-1 gap-3" onSubmit={handleAddUserSubmit}>
          <Input
            id="add-user-name"
            label={t("name", { defaultValue: "Name" })}
            value={addUserForm.name}
            onChange={(event) =>
              setAddUserForm((current) => ({ ...current, name: event.target.value }))
            }
            required
          />
          <Input
            id="add-user-email"
            label={t("email", { defaultValue: "Email" })}
            type="email"
            value={addUserForm.email}
            onChange={(event) =>
              setAddUserForm((current) => ({ ...current, email: event.target.value.toLowerCase().replace(/\s/g, "") }))
            }
            required
          />
          <Input
            id="add-user-phone"
            label={`${t("phone", { defaultValue: "Phone" })} (${t("phone-optional", { defaultValue: "optional" })})`}
            value={addUserForm.phone}
            onChange={(event) =>
              setAddUserForm((current) => ({ ...current, phone: event.target.value }))
            }
          />
          <div className="w-full">
            <label
              htmlFor="add-user-role"
              className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted"
            >
              {t("Role", { defaultValue: "Role" })}
            </label>
            <select
              id="add-user-role"
              className="w-full rounded-xl border border-app-line bg-white px-3 py-2.5 text-sm text-app-ink shadow-soft outline-none transition focus:border-app-brand focus:ring-4 focus:ring-app-brand/15"
              value={addUserForm.role}
              onChange={(event) =>
                setAddUserForm((current) => ({ ...current, role: event.target.value }))
              }
              required
            >
              <option value="">{t("Select", { defaultValue: "Select" })}</option>
              <option value="OrgAdmin">OrgAdmin</option>
              <option value="Editor">Editor</option>
              <option value="User">User</option>
            </select>
          </div>
          <div className="rounded-xl border border-app-line bg-app-paper px-3 py-2">
            <p className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
              {t("password", { defaultValue: "Password" })}
            </p>
            <div className="mt-1 flex items-center justify-between gap-2">
              <code className="truncate text-sm text-app-ink">{addUserForm.password}</code>
              <Button type="button" variant="ghost" className="px-3 py-1.5" onClick={copyGeneratedPassword}>
                {t("copy-to-clipboard", { defaultValue: "Copy to clipboard" })}
              </Button>
            </div>
            <p className="m-0 mt-1 text-[11px] text-app-ink-muted">
              {t("password-generateed", {
                defaultValue: "Password will only be generated once; make sure to copy it."
              })}
            </p>
          </div>
          <label
            htmlFor="add-user-send-onboarding-email"
            className="inline-flex items-center gap-2 rounded-xl border border-app-line bg-app-paper px-3 py-2 text-sm text-app-ink"
          >
            <input
              id="add-user-send-onboarding-email"
              type="checkbox"
              className="h-4 w-4 rounded border-app-line text-app-brand focus:ring-app-brand/30"
              checked={addUserForm.sendOnboardingEmail === true}
              onChange={(event) =>
                setAddUserForm((current) => ({
                  ...current,
                  sendOnboardingEmail: event.target.checked
                }))
              }
            />
            <span>
              {t("new-ui.settings.users.send-onboarding-email", {
                defaultValue: "Send onboarding email to the new user"
              })}
            </span>
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" disabled={isAddUserLoading}>
              {isAddUserLoading
                ? t("loading", { defaultValue: "Loading..." })
                : t("submit", { defaultValue: "Submit" })}
            </Button>
            <Button type="button" variant="ghost" onClick={closeAddUserModal}>
              {t("cancel", { defaultValue: "Cancel" })}
            </Button>
          </div>
        </form>
      </Modal>
      <Modal
        isOpen={isToggleUserModalOpen}
        title={t("user-status", { defaultValue: "User status" })}
        onClose={closeToggleUserModal}
      >
        <div className="grid grid-cols-1 gap-4">
          <p className="m-0 text-sm text-app-ink">
            {`${t("are-you-sure", { defaultValue: "Are you sure you want to" })} ${
              userPendingStatusChange?.active
                ? t("deactivate", { defaultValue: "deactivate" })
                : t("activate", { defaultValue: "activate" })
            }${t("this-user", { defaultValue: " this user" })}?`}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" onClick={handleConfirmUserStatusToggle} disabled={isToggleUserLoading}>
              {isToggleUserLoading
                ? t("loading", { defaultValue: "Loading..." })
                : t("yes", { defaultValue: "Yes" })}
            </Button>
            <Button type="button" variant="ghost" onClick={closeToggleUserModal} disabled={isToggleUserLoading}>
              {t("no", { defaultValue: "No" })}
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={isDeleteUserModalOpen}
        title={t("delete-user", { defaultValue: "Delete user" })}
        onClose={closeDeleteUserModal}
      >
        <div className="grid grid-cols-1 gap-4">
          <p className="m-0 text-sm text-app-ink">
            {t("new-ui.settings.users.delete-confirm", {
              defaultValue: "Are you sure you want to delete this user? This action cannot be undone."
            })}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="danger" onClick={handleDeleteUser} disabled={isDeleteUserLoading}>
              {isDeleteUserLoading
                ? t("loading", { defaultValue: "Loading..." })
                : t("delete", { defaultValue: "Delete" })}
            </Button>
            <Button type="button" variant="ghost" onClick={closeDeleteUserModal} disabled={isDeleteUserLoading}>
              {t("cancel", { defaultValue: "Cancel" })}
            </Button>
          </div>
        </div>
      </Modal>
    </AppShell>
  );
}

export default AccountSettingsPage;
